<template>
  <div
    class="financial-summary"
    v-if="summaryMessages.length > 0 || paymentMessages.length > 0 || balanceData || loading || loadingPayments || loadingBalance || showPaymentsSummaryShell || showProformCollectionStagesForUi"
  >
    <!-- Sin cotización cargada para la moneda de la operación, el margen queda vacío en vez
         de calcularse con un cambio 1:1 que no es real. -->
    <div
      v-if="operationCurrencyRateMissing"
      class="alert alert-warning p-2 mb-2 d-flex align-items-start"
      role="alert"
    >
      <i class="fas fa-exclamation-triangle me-2 mt-1"></i>
      <div class="small">
        {{
          $t("financialSummary.messages.missingCurrencyRate", {
            currency: missingRateCurrenciesLabel,
          })
        }}
      </div>
    </div>

    <ContentSkeleton v-if="collectionSummaryOnly && loading" variant="dashboard" :cols="3" />

    <CollectionSummary
      v-else-if="collectionSummaryOnly && showProformCollectionStagesForUi && collectionStagesVisual"
      :model-name="modelName"
      :item-data="itemData"
      :balance-data="balanceData"
      :financial-data="financialData"
      :can-collect="canCollect"
      :can-edit-shipper-prices="canMakePayments"
      :can-edit-invoice-unit-qty="canMakePayments"
      :can-autoload-expenses="canMakePayments"
      :can-load-negotiation-expenses="canLoadNegotiationExpenses"
      :loading-negotiation-expenses="loadingNegotiationExpenses"
      :can-make-payments="canMakePayments"
      :can-finance-manage-payments="canFinanceManagePayments"
      :can-delete-payments="canDeletePayments"
      :lists="lists"
      :silent-autoload-invoice-supplier-after-packinglist="fobtradingModuleActive"
      :fob-nota-comercial-shortcuts="fobtradingModuleActive"
      :nota-comercial-module-active="notaComercialModuleActive"
      @refresh="refreshCollectionStagesAfterChange"
      @payment-action="executeAction"
      @collection-saved="$emit('collection-saved', $event)"
      @shipping-saved="$emit('shipping-saved')"
      @advance-pct-saved="$emit('advance-pct-saved')"
      @edit-item="onCollectionSummaryEditItem"
      @load-negotiation-expenses="loadNegotiationExpenses"
    />

    <div v-if="!collectionSummaryOnly && !balanceOnly" class="card border-info mb-3">
      <div class="card-header bg-info bg-opacity-10 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="d-flex align-items-center flex-wrap gap-2 flex-grow-1 min-w-0">
          <h6 class="mb-0 d-flex align-items-center flex-wrap">
            <i class="fas fa-chart-line me-2 text-info"></i>
            {{ $t("financialSummary.title") }}
            <i
              class="fas fa-info-circle ms-2 text-info cursor-pointer"
              :title="$t('financialSummary.help')"
              @click="showHelp"
            ></i>
          </h6>
          <div
            v-if="resolvedCollectionClientId"
            class="small border-start border-secondary border-opacity-25 ps-2 ms-1 text-truncate"
            style="max-width: min(100%, 18rem)"
          >
            <span class="text-muted me-1">{{ $t("financialSummary.summaryClientLabel") }}</span>
            <a
              href="#"
              class="financial-summary-client-link text-body"
              :title="$t('financialSummary.openContactNewTab')"
              @click.prevent="goToContact(resolvedCollectionClientId)"
            >
              {{ resolvedCollectionClientName || $t("financialSummary.summaryClientFallback") }}
              <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
            </a>
            <NotaComercialPendingBadge
              class="ms-1"
              :contact-id="String(resolvedCollectionClientId)"
              :contact-name="resolvedCollectionClientName"
            />
          </div>
        </div>
        <button 
          class="btn btn-sm btn-outline-info flex-shrink-0"
          @click="refreshData"
          :disabled="loading"
        >
          <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
        </button>
      </div>
      <div class="card-body">
        <!-- Loading state -->
        <ContentSkeleton v-if="loading" variant="dashboard" :cols="3" />

        <!-- Summary messages (+ etapa 1 proforma sin embarques: layout visual) -->
        <div v-else-if="summaryMessages.length > 0 || showProformCollectionStagesForUi">
          <CollectionSummary
            v-if="showProformCollectionStagesForUi && collectionStagesVisual"
            :model-name="modelName"
            :item-data="itemData"
            :balance-data="balanceData"
            :financial-data="financialData"
            :can-collect="canCollect"
            :can-edit-shipper-prices="canMakePayments"
            :can-edit-invoice-unit-qty="canMakePayments"
            :can-autoload-expenses="canMakePayments"
            :can-load-negotiation-expenses="canLoadNegotiationExpenses"
            :loading-negotiation-expenses="loadingNegotiationExpenses"
            :can-make-payments="canMakePayments"
            :can-finance-manage-payments="canFinanceManagePayments"
            :can-delete-payments="canDeletePayments"
            :lists="lists"
            :silent-autoload-invoice-supplier-after-packinglist="fobtradingModuleActive"
            :fob-nota-comercial-shortcuts="fobtradingModuleActive"
            :nota-comercial-module-active="notaComercialModuleActive"
            @refresh="refreshCollectionStagesAfterChange"
            @payment-action="executeAction"
            @collection-saved="$emit('collection-saved', $event)"
            @shipping-saved="$emit('shipping-saved')"
            @advance-pct-saved="$emit('advance-pct-saved')"
            @edit-item="onCollectionSummaryEditItem"
            @load-negotiation-expenses="loadNegotiationExpenses"
          />

          <div
            v-for="(message, index) in summaryMessages"
            :key="index"
          >
            <div class="d-flex align-items-start">
              <div class="message-icon me-3">
                <i :class="message.icon" :style="{ color: message.color }"></i>
              </div>
              <div class="message-content flex-grow-1">
                <div class="message-text" v-html="message.text"></div>
                <small v-if="message.details" class="text-muted d-block mt-1">
                  {{ message.details }}
                </small>
              </div>
              <div
                v-if="
                  (message.action && (message.action.type !== 'addCollect' || canCollect)) ||
                  (notaComercialQuickAllowed && index === 0) ||
                  (showShippingOperacionAction && index === 0)
                "
                class="message-action ms-2 d-flex flex-wrap gap-2 justify-content-end"
              >
                <button
                  v-if="message.action && (message.action.type !== 'addCollect' || canCollect)"
                  class="btn btn-sm"
                  :class="message.action.class"
                  @click="executeAction(message.action)"
                >
                  <i :class="message.action.icon" class="me-1"></i>
                  {{ message.action.label }}
                </button>
                <button
                  v-if="notaComercialQuickAllowed && index === 0"
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="openNotaComercialNoteModal"
                >
                  <i class="fas fa-file-invoice me-1" aria-hidden="true"></i>
                  {{ $t("financialSummary.actions.addCommercialNote") }}
                </button>
                <NotaComercialAssignButton
                  v-if="index === 0 && resolvedCollectionClientId && ncOperationTargetId"
                  :contact-id="String(resolvedCollectionClientId)"
                  side="client"
                  :target-type="ncOperationTargetType"
                  :target-id="ncOperationTargetId"
                  @assigned="refreshData"
                />
                <button
                  v-if="showShippingOperacionAction && index === 0"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('fobtrading.listOpenOperacionView')"
                  :aria-label="$t('fobtrading.listOpenOperacionView')"
                  @click="openShippingOperacionView"
                >
                  <i class="fas fa-anchor me-1" aria-hidden="true"></i>
                  {{ $t("fobtrading.listOpenOperacionView") }}
                </button>
              </div>
            </div>
          </div>

          <!-- Summary totals -->
          <div
            v-if="financialData && summaryMessages.length > 0"
            class="financial-totals mt-4 pt-3 border-top"
          >
            <div class="row text-center">
              <div class="col-md-4 col-6 mb-2">
                <div class="total-item">
                  <small class="text-muted d-block">{{ isShipping ? $t("financialSummary.totalBilled") : $t("financialSummary.totalProform") }}</small>
                  <strong class="text-primary fs-5">{{ moneyPrefix }}{{ formatNumber(financialData.totalProform) }}</strong>
                  <small v-if="fxUsdHint(fxClientEntry, financialData.totalProform)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, financialData.totalProform) }}</small>
                  <small v-if="isShipping && financialData.proformAdvanceDeducted > 0" class="text-muted d-block">
                    - {{ moneyPrefix }}{{ formatNumber(financialData.proformAdvanceDeducted) }} {{ $t("financialSummary.advanceDeducted") }}
                  </small>
                  <small v-if="isShipping && financialData.shippingCollectedApplied > 0" class="text-muted d-block">
                    - {{ moneyPrefix }}{{ formatNumber(financialData.shippingCollectedApplied) }} {{ $t("financialSummary.shippingCollectionsApplied") }}
                  </small>
                  <small v-if="isShipping && financialData.point1CreditNotesGiven > 0.01" class="text-muted d-block">
                    - {{ moneyPrefix }}{{ formatNumber(financialData.point1CreditNotesGiven) }} {{ $t("financialSummary.commercialNotesCreditDeducted") }}
                  </small>
                  <small v-else-if="isShipping && financialData.point1CreditNotesGiven < -0.01" class="text-danger d-block">
                    + {{ moneyPrefix }}{{ formatNumber(Math.abs(financialData.point1CreditNotesGiven)) }} {{ $t("financialSummary.commercialNotesDebitAdded") }}
                  </small>
                </div>
              </div>
              <div class="col-md-4 col-6 mb-2">
                <div class="total-item">
                  <small class="text-muted d-block">{{ $t("financialSummary.collected") }}</small>
                  <strong class="text-success fs-5">
                    {{ moneyPrefix }}{{ formatNumber(financialData.totalCollected) }}
                    <button 
                      v-if="financialData.totalCollected > 0"
                      class="btn btn-link btn-sm p-0 ms-1" 
                      @click="viewCollectionDetails"
                      :title="$t('financialSummary.viewCollections')"
                    >
                      <i class="fas fa-eye text-info"></i>
                    </button>
                  </strong>
                  <small v-if="fxUsdHint(fxClientEntry, financialData.totalCollected)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, financialData.totalCollected) }}</small>
                </div>
              </div>
              <div class="col-md-4 col-6 mb-2">
                <div class="total-item">
                  <small class="text-muted d-block">
                    {{ isProform ? $t("financialSummary.pendingAdvance") : $t("financialSummary.pending") }}
                  </small>
                  <strong :class="financialData.totalPending > 0 ? 'text-danger' : financialData.totalOverCollected > 0.01 ? 'text-warning' : 'text-success'" class="fs-5">
                    {{ moneyPrefix }}{{ formatNumber(financialData.totalPending) }}
                  </strong>
                  <small v-if="fxUsdHint(fxClientEntry, financialData.totalPending)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, financialData.totalPending) }}</small>
                  <p
                    v-if="isShipping && Math.abs(financialData.point1CreditNotesGiven) > 0.01"
                    class="small text-muted mb-0 mt-1 lh-sm"
                    style="font-size: 0.75rem"
                  >
                    {{
                      $t("financialSummary.stages.notaImpactExpectedNet", {
                        amount:
                          financialData.point1CreditNotesGiven >= 0
                            ? "-" + moneyPrefix + formatNumber(financialData.point1CreditNotesGiven)
                            : "+" + moneyPrefix + formatNumber(Math.abs(financialData.point1CreditNotesGiven)),
                      })
                    }}
                  </p>
                  <p
                    v-if="financialData.totalOverCollected > 0.01"
                    class="small text-warning mb-0 mt-1 lh-sm"
                    style="font-size: 0.75rem"
                  >
                    {{
                      $t("financialSummary.overCollectedWarning", {
                        amount: moneyPrefix + formatNumber(financialData.totalOverCollected),
                      })
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No data state -->
        <div v-else class="text-muted text-center py-3">
          <i class="fas fa-check-circle fa-2x mb-2 d-block text-success"></i>
          {{ $t("financialSummary.allGood") }}
        </div>
      </div>
    </div>

    <!-- Payments Section (solo embarques con registro cargado) -->
    <div v-if="!collectionSummaryOnly && !balanceOnly && showPaymentsSummaryShell" class="card border-danger mb-3">
      <div class="card-header bg-danger bg-opacity-10 d-flex justify-content-between align-items-center">
        <h6 class="mb-0 d-flex align-items-center">
          <i class="fas fa-arrow-up me-2 text-danger"></i>
          {{ $t("financialSummary.payments.title") }}
          <button 
            v-if="hasPayments"
            class="btn btn-link btn-sm p-0 ms-2" 
            @click="viewPaymentDetails"
            :title="$t('financialSummary.viewPayments')"
          >
            <i class="fas fa-eye text-info"></i>
          </button>
        </h6>
        <button 
          class="btn btn-sm btn-outline-danger"
          @click="loadPaymentData"
          :disabled="loadingPayments"
        >
          <i :class="loadingPayments ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
        </button>
      </div>
      <div class="card-body">
        <!-- Loading state -->
        <ContentSkeleton v-if="loadingPayments" variant="list" :rows="3" />

        <!-- Payment messages -->
        <div v-else-if="paymentMessages.length > 0">
          <div
            v-for="(message, index) in paymentMessages"
            :key="'payment-' + index"
          >
            <div class="d-flex align-items-start">
              <div class="message-icon me-3">
                <i :class="message.icon" :style="{ color: message.color }"></i>
              </div>
              <div class="message-content flex-grow-1">
                <div class="message-text" v-html="message.text"></div>
                <small v-if="message.details" class="text-muted d-block mt-1">
                  {{ message.details }}
                </small>
                <!-- Notas ligadas a este gasto: sin esto el resumen era el único lugar
                     donde no se veían y había que bajar al operation balance. -->
                <div v-if="paymentMessageNotes(message).length" class="mt-1">
                  <div
                    v-for="note in paymentMessageNotes(message)"
                    :key="`pay-nc-${message.expenseId}-${note._id}`"
                    class="small d-flex align-items-center gap-1 flex-wrap"
                    style="font-size: 0.75rem"
                  >
                    <i
                      class="fas fa-file-invoice-dollar"
                      :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                      aria-hidden="true"
                    ></i>
                    <a
                      :href="operationBalanceNotaComercialHref(note._id)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-body text-decoration-none"
                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                    >{{ note.name || note.number }}</a>
                    <strong
                      :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                    >{{ operationBalanceSupplierNotaDisplay(note) }}</strong>
                    <a
                      v-if="notaComercialModuleActive && note._id && ncOperationTargetId"
                      href="#"
                      class="text-warning"
                      :title="$t('notaComercial.unapply.iconTitle')"
                      @click.prevent.stop="unapplyNotaFromBalanceRow(note, ncOperationTargetType, ncOperationTargetId)"
                    >
                      <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                  </div>
                </div>
              </div>
              <!-- Inline totals for this item -->
              <div v-if="message.totals" class="message-totals ms-auto">
                <small class="text-muted">
                  {{ $t("financialSummary.payments.total") }}: <strong 
                    :class="message.totals.total > 0 ? 'text-primary' : ''"
                    @click="hasPayments ? viewPaymentDetails() : null"
                    :style="hasPayments ? 'cursor: pointer;' : ''"
                    :title="hasPayments ? $t('financialSummary.viewPayments') : ''"
                  >{{ paymentMessagePrefix(message) }}{{ formatNumber(message.totals.total) }}</strong>
                  <span v-if="message.totals.requested > 0" class="ms-2">
                    | {{ $t("financialSummary.payments.requested") }}: <strong 
                      class="text-warning"
                      @click="viewPaymentDetails"
                      style="cursor: pointer;"
                      :title="$t('financialSummary.viewPayments')"
                    >{{ paymentMessagePrefix(message) }}{{ formatNumber(message.totals.requested) }}</strong>
                  </span>
                  <span class="ms-2">
                    | {{ $t("financialSummary.payments.paid") }}: <strong 
                      class="text-success"
                      @click="message.totals.paid > 0 ? viewPaymentDetails() : null"
                      :style="message.totals.paid > 0 ? 'cursor: pointer;' : ''"
                      :title="message.totals.paid > 0 ? $t('financialSummary.viewPayments') : ''"
                    >{{ paymentMessagePrefix(message) }}{{ formatNumber(message.totals.paid) }}</strong>
                  </span>
                  <span v-if="Math.abs(paymentMessageNotaNetEffect(message)) > 0.01" class="ms-2">
                    | {{ $t("financialSummary.payments.notes") }}: <strong
                      :class="paymentMessageNotaNetEffect(message) < 0 ? 'text-success' : 'text-danger'"
                      :title="$t('financialSummary.stages.shipPendingAfterNotesHint')"
                    >{{ paymentMessageNotaEffectLabel(message, moneyPrefix) }}</strong>
                  </span>
                  <span class="ms-2">
                    | {{ $t("financialSummary.payments.pending") }}: <strong 
                      :class="paymentMessagePendingAfterNotes(message) > 0 ? 'text-danger' : ''"
                      @click="paymentMessagePendingAfterNotes(message) > 0 ? viewPaymentDetails() : null"
                      :style="paymentMessagePendingAfterNotes(message) > 0 ? 'cursor: pointer;' : ''"
                      :title="paymentMessagePendingAfterNotes(message) > 0 ? $t('financialSummary.viewPayments') : ''"
                    >{{ paymentMessagePrefix(message) }}{{ formatNumber(paymentMessagePendingAfterNotes(message)) }}</strong>
                  </span>
                </small>
              </div>
              <!-- Nota comercial del proveedor de este gasto -->
              <div
                v-if="notaComercialQuickAllowed && (message.expenseId || message.expenseContactId)"
                class="message-action ms-2 d-flex align-items-start"
              >
                <button
                  v-if="message.expenseId"
                  type="button"
                  class="btn btn-sm btn-outline-secondary text-nowrap"
                  :title="$t('financialSummary.fobNotaComercial.addSupplierNoteHint')"
                  @click="openSupplierNoteForExpense(message.expenseId)"
                >
                  <i class="fas fa-file-invoice me-1" aria-hidden="true"></i>
                  {{ $t("financialSummary.fobNotaComercial.addSupplierNote") }}
                </button>
                <NotaComercialAssignButton
                  v-if="message.expenseContactId && ncOperationTargetId"
                  :contact-id="message.expenseContactId"
                  side="supplier"
                  :target-type="ncOperationTargetType"
                  :target-id="ncOperationTargetId"
                  :expense-id="String(message.expenseId || '')"
                  @assigned="refreshData"
                />
              </div>
              <!-- Single action (siempre visible; deshabilitada con explicación si aplica) -->
              <div
                v-if="message.action && !message.actions"
                class="message-action ms-2 d-flex flex-column align-items-end gap-1"
              >
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="message.action.class"
                  :disabled="paymentSectionActionDisabled(message.action)"
                  :title="paymentSectionActionDisabledHint(message.action) || undefined"
                  @click="onPaymentSectionActionClick(message.action)"
                >
                  <i :class="message.action.icon" class="me-1"></i>
                  {{ message.action.label }}
                </button>
                <small
                  v-if="paymentSectionActionDisabledHint(message.action)"
                  class="text-muted text-end"
                  style="max-width: 14rem; font-size: 0.75rem; line-height: 1.2"
                >
                  {{ paymentSectionActionDisabledHint(message.action) }}
                </small>
              </div>
              <!-- Multiple actions -->
              <div
                v-if="message.actions && message.actions.length"
                class="message-actions ms-2 d-flex flex-column gap-1 align-items-end"
              >
                <div
                  v-for="(action, actionIndex) in message.actions"
                  :key="'action-' + actionIndex"
                  class="d-flex flex-column align-items-end gap-1"
                >
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="action.class"
                    :disabled="paymentSectionActionDisabled(action)"
                    :title="paymentSectionActionDisabledHint(action) || undefined"
                    @click="onPaymentSectionActionClick(action)"
                  >
                    <i :class="action.icon" class="me-1"></i>
                    {{ action.label }}
                  </button>
                  <small
                    v-if="paymentSectionActionDisabledHint(action)"
                    class="text-muted text-end"
                    style="max-width: 14rem; font-size: 0.75rem; line-height: 1.2"
                  >
                    {{ paymentSectionActionDisabledHint(action) }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sin datos (p. ej. aún no cargó la API) -->
        <div v-else class="text-muted text-center py-3">
          <i class="fas fa-spinner fa-spin fa-2x mb-2 d-block text-secondary"></i>
          {{ $t("financialSummary.payments.summaryPreparing") }}
        </div>
      </div>
    </div>

    <!-- Operation Balance Section (for both proforms and shippings) -->
    <div v-if="isShipping || isProform" class="card border-success mb-3">
      <div class="card-header bg-success bg-opacity-10 d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="fas fa-balance-scale me-2 text-success"></i>
          {{ $t("financialSummary.balance.title") }}
          <span
            v-if="balanceData && balanceData.isProform && balanceData.proform && balanceData.proform.qtycontainers"
            class="text-muted ms-2"
          >
            ({{ balanceData.proform.qtycontainers }} {{ balanceData.proform.qtycontainers === 1 ? 'container' : 'containers' }})
          </span>
          <span
            v-else-if="balanceData && !balanceData.isProform && itemData && itemData.qtycontainers"
            class="text-muted ms-2"
          >
            ({{ itemData.qtycontainers }} {{ itemData.qtycontainers === 1 ? 'container' : 'containers' }})
          </span>
        </h6>
        <div class="d-flex gap-2">
          <!-- GenerateAllEntriesButton oculto según solicitud -->
          <!-- <GenerateAllEntriesButton
            v-if="itemData && itemData._id"
            :operationType="isShipping ? 'shipping' : 'proform'"
            :operationId="itemData._id"
            @entries-generated="onEntriesGenerated"
          /> -->
          <button 
            class="btn btn-sm btn-outline-success"
            @click="loadBalanceData"
            :disabled="loadingBalance"
          >
            <i :class="loadingBalance ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Loading state -->
        <ContentSkeleton v-if="loadingBalance" variant="table" :rows="5" :cols="4" />

        <!-- Balance Table -->
        <div v-else-if="balanceData" class="balance-table">
        

          <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
              <thead class="table-">
                <tr>
                  <th class="text-center" :style="{ width: balanceFx ? '20%' : '25%' }">{{ $t("financialSummary.balance.concept") }}</th>
                  <th class="text-end text-primary" :style="{ width: balanceFx ? '13%' : '18.75%' }">{{ $t("financialSummary.balance.totalAmount") }}</th>
                  <!-- Tipo de cambio de la operación y equivalente en la moneda del sistema (#397) -->
                  <template v-if="balanceFx">
                    <th class="text-center text-nowrap" style="width: 12%" :title="$t('exchangeRates.columns.rateHint')">{{ $t("exchangeRates.columns.rate") }}</th>
                    <th class="text-end text-nowrap" style="width: 16%">{{ $t("exchangeRates.columns.totalConverted", { currency: fxReportingLabel }) }}</th>
                  </template>
                  <th class="text-end text-success" :style="{ width: balanceFx ? '13%' : '18.75%' }">{{ $t("financialSummary.balance.paid") }}</th>
                  <th class="text-end text-danger" :style="{ width: balanceFx ? '13%' : '18.75%' }">{{ $t("financialSummary.balance.pending") }}</th>
                  <th class="text-center" :style="{ width: balanceFx ? '13%' : '18.75%' }">{{ $t("financialSummary.balance.status") }}</th>
                </tr>
              </thead>
              <tbody>
                <!-- Client Section Header -->
                <tr class="table-info">
                  <td :colspan="balanceColspan" class="fw-bold d-flex justify-content-between align-items-center">
                    <span>
                      <i class="fas fa-arrow-down me-2"></i>
                      {{ $t("financialSummary.balance.clientSection") }}
                    </span>
                    <button 
                      v-if="balanceData.client.collected > 0 || balanceData.client.balance > 0"
                      class="btn btn-link btn-sm p-0 ms-2" 
                      @click="viewCollectionDetails"
                      :title="$t('financialSummary.viewCollections')"
                    >
                      <i class="fas fa-eye text-info"></i>
                    </button>
                  </td>
                </tr>
                <!-- Proforma Total (only for proformas) -->
                <tr v-if="balanceData.isProform && balanceData.client.totalProform">
                  <td>
                    <i class="fas fa-file-alt text-info me-2"></i>
                    {{ $t("financialSummary.balance.proformaTotal") }}
                  </td>
                  <td class="text-end fw-bold" :class="balanceData.client.totalProform > 0 ? 'text-primary' : ''">{{ moneyPrefix }}{{ formatNumber(balanceData.client.totalProform) }}</td>
                  <template v-if="balanceFx"><td></td><td></td></template>
                  <td class="text-end text-muted">-</td>
                  <td class="text-end text-muted">-</td>
                  <td class="text-center">
                    <span class="badge bg-info">{{ $t("financialSummary.balance.projected") }}</span>
                  </td>
                </tr>
                <!-- Client Invoice / Total Invoices -->
                <!-- Show per-shipping breakdown if proforma with shippings -->
                <template v-if="balanceData.isProform && balanceData.shippings && balanceData.shippings.length > 0">
                  <template v-for="shipping in balanceData.shippings">
                    <tr :key="shipping._id">
                      <td>
                        <i class="fas fa-file-invoice text-primary me-2"></i>
                        <span 
                          @click="goToShipping(shipping._id)"
                          style="cursor: pointer; text-decoration: underline;"
                          :title="$t('financialSummary.viewShipping')"
                          class="text-primary"
                        >
                          {{ shipping.name }}
                        </span>
                        <small v-if="shipping.invoiceNumber" class="text-muted ms-2">
                          ({{ $t("financialSummary.balance.invoice") }} #{{ shipping.invoiceNumber }})
                        </small>
                      </td>
                      <td class="text-end fw-bold" :class="shipping.invoice > 0 ? 'text-primary' : ''">{{ moneyPrefix }}{{ formatNumber(shipping.invoice) }}</td>
                      <template v-if="balanceFx">
                        <OperationExchangeRateCell :entry="fxEntryForParts(fxShippingInvoiceParts(shipping))" @open="openFxModal" />
                        <td class="text-end tabular-nums text-nowrap">{{ fxUsdPartsDisplay(fxShippingInvoiceParts(shipping)) }}</td>
                      </template>
                      <td class="text-end" :class="shipping.collected > 0 ? 'text-success' : ''">
                        <div class="d-flex flex-column align-items-end">
                          <span 
                            v-if="shipping.collected > 0"
                            @click="viewCollectionDetails"
                            style="cursor: pointer;"
                            :title="$t('financialSummary.viewCollections')"
                          >
                            {{ moneyPrefix }}{{ formatNumber(shipping.collected) }}
                          </span>
                          <span v-else>{{ moneyPrefix }}{{ formatNumber(shipping.collected) }}</span>
                          <small v-if="fxUsdHint(fxEntryForParts(fxShippingInvoiceParts(shipping)), shipping.collected)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxShippingInvoiceParts(shipping)), shipping.collected) }}</small>
                          <small v-if="shipping.advancePortion > 0" class="text-muted" style="font-size: 0.75rem;">
                            ({{ $t("financialSummary.balance.advancePortion") || "Advance" }}: {{ moneyPrefix }}{{ formatNumber(shipping.advancePortion) }})
                          </small>
                        </div>
                      </td>
                      <td 
                        class="text-end"
                        :class="clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending > 0 ? 'text-danger' : clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).overCollected > 0.01 ? 'text-warning' : ''"
                        @click="clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending > 0 ? viewCollectionDetails() : null"
                        :style="clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending > 0 ? 'cursor: pointer;' : ''"
                        :title="clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending > 0 ? $t('financialSummary.viewCollections') : ''"
                      >
                        <div>{{ moneyPrefix }}{{ formatNumber(clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending) }}</div>
                        <small v-if="fxUsdHint(fxEntryForParts(fxShippingInvoiceParts(shipping)), clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxShippingInvoiceParts(shipping)), clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).pending) }}</small>
                        <p
                          v-if="clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).overCollected > 0.01"
                          class="small text-warning mb-0 mt-1 lh-sm"
                          style="font-size: 0.7rem"
                        >
                          {{
                            $t("financialSummary.overCollectedWarning", {
                              amount:
                                moneyPrefix + formatNumber(
                                  clientBalancePendingDisplay(
                                    shipping.clientBalance,
                                    shipping.overCollectedOnInvoice
                                  ).overCollected
                                ),
                            })
                          }}
                        </p>
                      </td>
                      <td class="text-center">
                        <span 
                          class="badge" 
                          :class="
                            clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).overCollected > 0.01
                              ? 'bg-warning text-dark'
                              : isEffectivelyZero(shipping.clientBalance)
                                ? 'bg-success'
                                : shipping.collected > 0
                                  ? 'bg-warning'
                                  : 'bg-danger'
                          "
                        >
                          {{
                            clientBalancePendingDisplay(shipping.clientBalance, shipping.overCollectedOnInvoice).overCollected > 0.01
                              ? $t("financialSummary.balance.overCollectedStatus")
                              : isEffectivelyZero(shipping.clientBalance)
                                ? $t("financialSummary.balance.complete")
                                : shipping.collected > 0
                                  ? $t("financialSummary.balance.partial")
                                  : $t("financialSummary.balance.pendingStatus")
                          }}
                        </span>
                      </td>
                    </tr>
                    <tr
                      v-if="operationBalanceCollectionsSubrowAmount(shipping) > 0.01"
                      :key="`${shipping._id}-collections`"
                      class="table-light"
                    >
                      <td class="ps-4 small text-muted">
                        <i class="fas fa-receipt me-2"></i>
                        {{ $t("financialSummary.collections") }}
                      </td>
                      <td class="text-end text-muted">-</td>
                      <template v-if="balanceFx"><td></td><td></td></template>
                      <td
                        class="text-end text-success small"
                        @click="viewCollectionDetails"
                        style="cursor: pointer;"
                        :title="$t('financialSummary.viewCollections')"
                      >
                        {{ moneyPrefix }}{{ formatNumber(operationBalanceCollectionsSubrowAmount(shipping)) }}
                      </td>
                      <td class="text-end text-muted">-</td>
                      <td></td>
                    </tr>
                    <tr
                      v-for="note in operationBalanceClientCreditNotes(shipping)"
                      :key="`${shipping._id}-nc-${note._id}`"
                      class="table-light"
                    >
                      <td class="ps-4 small">
                        <i
                          class="fas fa-file-invoice me-2"
                          :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                        ></i>
                        <a
                          :href="operationBalanceNotaComercialHref(note._id)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-body text-decoration-none"
                          :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                        >
                          {{ note.name || note.number || $t('financialSummary.fobNotaComercial.notaComercialButton') }}
                          <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                        </a>
                        <a
                          v-if="notaComercialModuleActive && note._id && notaComercialHasPdf(note)"
                          href="#"
                          class="text-primary ms-1"
                          :title="$t('notaComercial.pdf.openTitle')"
                          @click.prevent="openNotaComercialPdf(note._id)"
                        >
                          <i class="fas fa-file-pdf" style="font-size: 0.65rem" aria-hidden="true"></i>
                        </a>
                        <a
                          v-if="notaComercialModuleActive && note._id && String(shipping._id)"
                          href="#"
                          class="text-warning ms-1"
                          :title="$t('notaComercial.unapply.iconTitle')"
                          @click.prevent.stop="unapplyNotaFromBalanceRow(note, 'shipping', String(shipping._id))"
                        >
                          <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                        </a>
                      </td>
                      <td class="text-end text-muted">-</td>
                      <template v-if="balanceFx"><td></td><td></td></template>
                      <td
                        class="text-end small"
                        :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                      >
                        {{ operationBalanceNotaCollectedDisplay(note) }}
                      </td>
                      <td class="text-end text-muted">-</td>
                      <td></td>
                    </tr>
                  </template>
                  <tr
                    v-for="note in operationBalanceProformCreditNotes"
                    :key="`proform-nc-${note._id}`"
                    class="table-light"
                  >
                    <td class="ps-4 small">
                      <i
                        class="fas fa-file-invoice me-2"
                        :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                      ></i>
                      <a
                        :href="operationBalanceNotaComercialHref(note._id)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-body text-decoration-none"
                        :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                      >
                        {{ note.name || note.number || $t('financialSummary.fobNotaComercial.notaComercialButton') }}
                        <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                      </a>
                      <a
                        v-if="notaComercialModuleActive && note._id && notaComercialHasPdf(note)"
                        href="#"
                        class="text-primary ms-1"
                        :title="$t('notaComercial.pdf.openTitle')"
                        @click.prevent="openNotaComercialPdf(note._id)"
                      >
                        <i class="fas fa-file-pdf" style="font-size: 0.65rem" aria-hidden="true"></i>
                      </a>
                      <a
                        v-if="notaComercialModuleActive && note._id && ncOperationTargetId"
                        href="#"
                        class="text-warning ms-1"
                        :title="$t('notaComercial.unapply.iconTitle')"
                        @click.prevent.stop="unapplyNotaFromBalanceRow(note, ncOperationTargetType, ncOperationTargetId)"
                      >
                        <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                      </a>
                      <small class="text-muted ms-1">({{ $t("financialSummary.balance.proformaTotal") }})</small>
                    </td>
                    <td class="text-end text-muted">-</td>
                    <template v-if="balanceFx"><td></td><td></td></template>
                    <td
                      class="text-end small"
                      :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                    >
                      {{ operationBalanceNotaCollectedDisplay(note) }}
                    </td>
                    <td class="text-end text-muted">-</td>
                    <td></td>
                  </tr>
                  <!-- Total row -->
                  <tr class="table-secondary">
                    <td class="fw-bold">
                      <i class="fas fa-calculator text-primary me-2"></i>
                      {{ $t("financialSummary.balance.totalInvoices") }}
                    </td>
                    <td class="text-end fw-bold" :class="balanceData.client.totalInvoices > 0 ? 'text-primary' : ''">{{ moneyPrefix }}{{ formatNumber(balanceData.client.totalInvoices) }}</td>
                    <template v-if="balanceFx">
                      <OperationExchangeRateCell :entry="fxEntryForParts(fxClientInvoiceParts)" @open="openFxModal" />
                      <td class="text-end fw-bold tabular-nums text-nowrap">{{ fxUsdPartsDisplay(fxClientInvoiceParts) }}</td>
                    </template>
                    <td
                      class="text-end fw-bold"
                      :class="balanceData.client.collected > 0 ? 'text-success' : ''"
                      @click="balanceData.client.collected > 0 ? viewCollectionDetails() : null"
                      :style="balanceData.client.collected > 0 ? 'cursor: pointer;' : ''"
                      :title="balanceData.client.collected > 0 ? $t('financialSummary.viewCollections') : ''"
                    >
                      {{ moneyPrefix }}{{ formatNumber(balanceData.client.collected) }}
                      <small v-if="fxUsdHint(fxClientEntry, balanceData.client.collected)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, balanceData.client.collected) }}</small>
                    </td>
                    <td 
                      class="text-end fw-bold"
                      :class="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? 'text-danger' : clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01 ? 'text-warning' : ''"
                      @click="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? viewCollectionDetails() : null"
                      :style="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? 'cursor: pointer;' : ''"
                      :title="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? $t('financialSummary.viewCollections') : ''"
                    >
                      <div>{{ moneyPrefix }}{{ formatNumber(clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending) }}</div>
                      <small v-if="fxUsdHint(fxClientEntry, clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending) }}</small>
                      <p
                        v-if="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01"
                        class="small text-warning mb-0 mt-1 lh-sm fw-normal"
                        style="font-size: 0.7rem"
                      >
                        {{
                          $t("financialSummary.overCollectedWarning", {
                            amount:
                              moneyPrefix + formatNumber(
                                clientBalancePendingDisplay(
                                  balanceData.client.balance,
                                  balanceData.client.overCollected
                                ).overCollected
                              ),
                          })
                        }}
                      </p>
                    </td>
                    <td class="text-center">
                      <span 
                        class="badge" 
                        :class="
                          clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01
                            ? 'bg-warning text-dark'
                            : isEffectivelyZero(balanceData.client.balance)
                              ? 'bg-success'
                              : balanceData.client.collected > 0
                                ? 'bg-warning'
                                : 'bg-danger'
                        "
                      >
                        {{
                          clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01
                            ? $t("financialSummary.balance.overCollectedStatus")
                            : isEffectivelyZero(balanceData.client.balance)
                              ? $t("financialSummary.balance.complete")
                              : balanceData.client.collected > 0
                                ? $t("financialSummary.balance.partial")
                                : $t("financialSummary.balance.pendingStatus")
                        }}
                      </span>
                    </td>
                  </tr>
                </template>
                <!-- Show single line for shipping or proforma without shippings -->
                <tr v-else>
                  <td>
                    <i class="fas fa-file-invoice text-primary me-2"></i>
                    {{ balanceData.isProform ? $t("financialSummary.balance.totalInvoices") : $t("financialSummary.balance.totalInvoice") }}
                  </td>
                  <td class="text-end fw-bold" :class="(balanceData.isProform ? balanceData.client.totalInvoices : balanceData.client.totalInvoice) > 0 ? 'text-primary' : ''">{{ moneyPrefix }}{{ formatNumber(balanceData.isProform ? balanceData.client.totalInvoices : balanceData.client.totalInvoice) }}</td>
                  <template v-if="balanceFx">
                    <OperationExchangeRateCell :entry="fxEntryForParts(fxClientInvoiceParts)" @open="openFxModal" />
                    <td class="text-end tabular-nums text-nowrap">{{ fxUsdPartsDisplay(fxClientInvoiceParts) }}</td>
                  </template>
                  <td 
                    class="text-end"
                    :class="balanceData.client.collected > 0 ? 'text-success' : ''"
                    @click="balanceData.client.collected > 0 ? viewCollectionDetails() : null"
                    :style="balanceData.client.collected > 0 ? 'cursor: pointer;' : ''"
                    :title="balanceData.client.collected > 0 ? $t('financialSummary.viewCollections') : ''"
                  >
                    {{ moneyPrefix }}{{ formatNumber(balanceData.client.collected) }}
                    <small v-if="fxUsdHint(fxClientEntry, balanceData.client.collected)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, balanceData.client.collected) }}</small>
                  </td>
                  <td 
                    class="text-end"
                    :class="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? 'text-danger' : clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01 ? 'text-warning' : ''"
                    @click="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? viewCollectionDetails() : null"
                    :style="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? 'cursor: pointer;' : ''"
                    :title="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending > 0 ? $t('financialSummary.viewCollections') : ''"
                  >
                    <div>{{ moneyPrefix }}{{ formatNumber(clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending) }}</div>
                    <small v-if="fxUsdHint(fxClientEntry, clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).pending) }}</small>
                    <p
                      v-if="clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01"
                      class="small text-warning mb-0 mt-1 lh-sm"
                      style="font-size: 0.7rem"
                    >
                      {{
                        $t("financialSummary.overCollectedWarning", {
                          amount:
                            moneyPrefix + formatNumber(
                              clientBalancePendingDisplay(
                                balanceData.client.balance,
                                balanceData.client.overCollected
                              ).overCollected
                            ),
                        })
                      }}
                    </p>
                  </td>
                  <td class="text-center">
                    <span 
                      class="badge" 
                      :class="
                        clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01
                          ? 'bg-warning text-dark'
                          : isEffectivelyZero(balanceData.client.balance)
                            ? 'bg-success'
                            : balanceData.client.collected > 0
                              ? 'bg-warning'
                              : 'bg-danger'
                      "
                    >
                      {{
                        clientBalancePendingDisplay(balanceData.client.balance, balanceData.client.overCollected).overCollected > 0.01
                          ? $t("financialSummary.balance.overCollectedStatus")
                          : isEffectivelyZero(balanceData.client.balance)
                            ? $t("financialSummary.balance.complete")
                            : balanceData.client.collected > 0
                              ? $t("financialSummary.balance.partial")
                              : $t("financialSummary.balance.pendingStatus")
                      }}
                    </span>
                  </td>
                </tr>
                <tr
                  v-for="note in operationBalanceShippingCreditNotes"
                  :key="`shipping-nc-${note._id}`"
                  class="table-light"
                >
                  <td class="ps-4 small">
                    <i
                      class="fas fa-file-invoice me-2"
                      :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                    ></i>
                    <a
                      :href="operationBalanceNotaComercialHref(note._id)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-body text-decoration-none"
                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                    >
                      {{ note.name || note.number || $t('financialSummary.fobNotaComercial.notaComercialButton') }}
                      <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                    <a
                      v-if="notaComercialModuleActive && note._id && notaComercialHasPdf(note)"
                      href="#"
                      class="text-primary ms-1"
                      :title="$t('notaComercial.pdf.openTitle')"
                      @click.prevent="openNotaComercialPdf(note._id)"
                    >
                      <i class="fas fa-file-pdf" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                    <a
                      v-if="notaComercialModuleActive && note._id && ncOperationTargetId"
                      href="#"
                      class="text-warning ms-1"
                      :title="$t('notaComercial.unapply.iconTitle')"
                      @click.prevent.stop="unapplyNotaFromBalanceRow(note, ncOperationTargetType, ncOperationTargetId)"
                    >
                      <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                  </td>
                  <td class="text-end text-muted">-</td>
                  <template v-if="balanceFx"><td></td><td></td></template>
                  <td
                    class="text-end small"
                    :class="operationBalanceNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                  >
                    {{ operationBalanceNotaCollectedDisplay(note) }}
                  </td>
                  <td class="text-end text-muted">-</td>
                  <td></td>
                </tr>

                <!-- Supplier Section Header -->
                <tr class="table-danger">
                  <td :colspan="balanceColspan" class="fw-bold d-flex justify-content-between align-items-center">
                    <span>
                      <i class="fas fa-arrow-up me-2"></i>
                      {{ $t("financialSummary.balance.supplierSection") }}
                    </span>
                    <button 
                      v-if="(balanceData.supplier.paid > 0 || balanceData.supplier.balance > 0) || (balanceData.costsSummary && (balanceData.costsSummary.paid > 0 || balanceData.costsSummary.balance > 0))"
                      class="btn btn-link btn-sm p-0 ms-2" 
                      @click="viewPaymentDetails"
                      :title="$t('financialSummary.viewPayments')"
                    >
                      <i class="fas fa-eye text-info"></i>
                    </button>
                  </td>
                </tr>
                <!-- Supplier Invoice -->
                <tr>
                  <td>
                    <i class="fas fa-file-invoice-dollar text-danger me-2"></i>
                    {{ $t("financialSummary.balance.supplierInvoice") }}
                  </td>
                  <td class="text-end fw-bold" :class="balanceSupplierExpenseDueDisplay > 0 ? 'text-primary' : ''">
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas('__supplier__', 'amount')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownAmountHint')"
                        @click.stop="openExpenseBreakdown('__supplier__', 'amount', $t('financialSummary.balance.supplierInvoice'))"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ supplierAmountDisplay("due") }}</span>
                    </span>
                  </td>
                  <template v-if="balanceFx">
                    <OperationExchangeRateCell :entry="fxEntryForParts(fxSupplierParts)" @open="openFxModal" />
                    <td class="text-end tabular-nums text-nowrap">{{ fxUsdPartsDisplay(fxSupplierParts) }}</td>
                  </template>
                  <td 
                    class="text-end"
                    :class="balanceData.supplier.paid > 0 ? 'text-success' : ''"
                    @click="(balanceData.supplier.paid > 0 || balanceData.supplier.balance > 0) ? viewPaymentDetails() : null"
                    :style="(balanceData.supplier.paid > 0 || balanceData.supplier.balance > 0) ? 'cursor: pointer;' : ''"
                    :title="(balanceData.supplier.paid > 0 || balanceData.supplier.balance > 0) ? $t('financialSummary.viewPayments') : ''"
                  >
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas('__supplier__', 'paid')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownPaidHint')"
                        @click.stop="openExpenseBreakdown('__supplier__', 'paid', $t('financialSummary.balance.supplierInvoice'))"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ supplierAmountDisplay("paid") }}</span>
                    </span>
                      <small v-if="fxUsdHint(fxEntryForParts(fxSupplierParts), balanceData.supplier.paid)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxSupplierParts), balanceData.supplier.paid) }}</small>
                  </td>
                  <td 
                    class="text-end"
                    :class="balanceData.supplier.balance > 0 ? 'text-danger' : ''"
                    @click="balanceData.supplier.balance > 0 ? viewPaymentDetails() : null"
                    :style="balanceData.supplier.balance > 0 ? 'cursor: pointer;' : ''"
                    :title="balanceData.supplier.balance > 0 ? $t('financialSummary.viewPayments') : ''"
                  >
                    {{ supplierAmountDisplay("balance") }}
                    <small v-if="fxUsdHint(fxEntryForParts(fxSupplierParts), balanceData.supplier.balance)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxSupplierParts), balanceData.supplier.balance) }}</small>
                  </td>
                  <td class="text-center">
                    <span 
                      class="badge" 
                      :class="balanceData.supplier.balance === 0 ? 'bg-success' : balanceData.supplier.paid > 0 ? 'bg-warning' : 'bg-danger'"
                    >
                      {{ balanceData.supplier.balance === 0 ? $t("financialSummary.balance.complete") : balanceData.supplier.paid > 0 ? $t("financialSummary.balance.partial") : $t("financialSummary.balance.pendingStatus") }}
                    </span>
                  </td>
                </tr>

                <!-- NC/ND del proveedor aplicadas a la factura (el pendiente ya las descuenta) -->
                <tr
                  v-for="note in operationBalanceSupplierNotes"
                  :key="`supplier-nc-${note._id}`"
                  class="table-light"
                >
                  <td class="ps-4 small">
                    <i
                      class="fas fa-file-invoice-dollar me-2"
                      :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                    ></i>
                    <a
                      :href="operationBalanceNotaComercialHref(note._id)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-body text-decoration-none"
                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                    >
                      {{ note.name || note.number || $t('financialSummary.fobNotaComercial.notaComercialButton') }}
                      <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                    <a
                      v-if="notaComercialModuleActive && note._id && ncOperationTargetId"
                      href="#"
                      class="text-warning ms-1"
                      :title="$t('notaComercial.unapply.iconTitle')"
                      @click.prevent.stop="unapplyNotaFromBalanceRow(note, ncOperationTargetType, ncOperationTargetId)"
                    >
                      <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                  </td>
                  <td class="text-end text-muted">-</td>
                  <template v-if="balanceFx"><td></td><td></td></template>
                  <td
                    class="text-end small"
                    :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                  >
                    {{ operationBalanceSupplierNotaDisplay(note) }}
                  </td>
                  <td class="text-end text-muted">-</td>
                  <td></td>
                </tr>

                <!-- Individual Costs: cada costo con las notas comerciales que le cuelgan -->
                <template v-for="cost in balanceData.costs">
                <tr :key="`cost-${cost.name}-${cost.currencyId || ''}`">
                  <td>
                    <i class="fas fa-tag text-secondary me-2"></i>
                    {{ cost.name }}
                  </td>
                  <td class="text-end" :class="cost.amount > 0 ? 'text-primary' : ''">
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas(cost.name, 'amount')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownAmountHint')"
                        @click.stop="openExpenseBreakdown(cost.name, 'amount', cost.name)"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ currencyPrefixFor(cost.currencyId) }}{{ formatNumber(cost.amount) }}</span>
                    </span>
                  </td>
                  <template v-if="balanceFx">
                    <OperationExchangeRateCell :entry="fxEntryForParts(fxCostParts(cost))" @open="openFxModal" />
                    <td class="text-end tabular-nums text-nowrap">{{ fxUsdPartsDisplay(fxCostParts(cost)) }}</td>
                  </template>
                  <td 
                    class="text-end"
                    :class="cost.paid > 0 ? 'text-success' : ''"
                    @click="(cost.paid > 0 || cost.balance > 0) ? viewPaymentDetails() : null"
                    :style="(cost.paid > 0 || cost.balance > 0) ? 'cursor: pointer;' : ''"
                    :title="(cost.paid > 0 || cost.balance > 0) ? $t('financialSummary.viewPayments') : ''"
                  >
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas(cost.name, 'paid')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownPaidHint')"
                        @click.stop="openExpenseBreakdown(cost.name, 'paid', cost.name)"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ currencyPrefixFor(cost.currencyId) }}{{ formatNumber(cost.paid) }}</span>
                    </span>
                      <small v-if="fxUsdHint(fxEntryForParts(fxCostParts(cost)), cost.paid)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxCostParts(cost)), cost.paid) }}</small>
                  </td>
                  <td 
                    class="text-end"
                    :class="cost.balance > 0 ? 'text-danger' : ''"
                    @click="cost.balance > 0 ? viewPaymentDetails() : null"
                    :style="cost.balance > 0 ? 'cursor: pointer;' : ''"
                    :title="cost.balance > 0 ? $t('financialSummary.viewPayments') : ''"
                  >
                    {{ currencyPrefixFor(cost.currencyId) }}{{ formatNumber(cost.balance) }}
                    <small v-if="fxUsdHint(fxEntryForParts(fxCostParts(cost)), cost.balance)" class="d-block text-muted fw-normal" style="font-size: 0.72rem">{{ fxUsdHint(fxEntryForParts(fxCostParts(cost)), cost.balance) }}</small>
                  </td>
                  <td class="text-center">
                    <span 
                      class="badge" 
                      :class="cost.balance === 0 ? 'bg-success' : cost.paid > 0 ? 'bg-warning' : 'bg-secondary'"
                    >
                      {{ cost.amount === 0 ? '-' : cost.balance === 0 ? $t("financialSummary.balance.complete") : cost.paid > 0 ? $t("financialSummary.balance.partial") : $t("financialSummary.balance.pendingStatus") }}
                    </span>
                  </td>
                </tr>
                <!-- NC/ND ligadas a ese gasto: van bajo su costo, no bajo la factura del proveedor -->
                <tr
                  v-for="note in operationBalanceNotesForCost(cost)"
                  :key="`cost-nc-${cost._id}-${note._id}`"
                  class="table-light"
                >
                  <td class="ps-4 small">
                    <i
                      class="fas fa-file-invoice-dollar me-2"
                      :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                    ></i>
                    <a
                      :href="operationBalanceNotaComercialHref(note._id)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-body text-decoration-none"
                      :title="$t('financialSummary.stages.openNotaComercialNewTab')"
                    >
                      {{ note.name || note.number || $t('financialSummary.fobNotaComercial.notaComercialButton') }}
                      <i class="fas fa-external-link-alt ms-1 opacity-50" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                    <a
                      v-if="notaComercialModuleActive && note._id && ncOperationTargetId"
                      href="#"
                      class="text-warning ms-1"
                      :title="$t('notaComercial.unapply.iconTitle')"
                      @click.prevent.stop="unapplyNotaFromBalanceRow(note, ncOperationTargetType, ncOperationTargetId)"
                    >
                      <i class="fas fa-unlink" style="font-size: 0.65rem" aria-hidden="true"></i>
                    </a>
                  </td>
                  <td class="text-end text-muted">-</td>
                  <template v-if="balanceFx"><td></td><td></td></template>
                  <td
                    class="text-end small"
                    :class="operationBalanceSupplierNotaIsDebit(note) ? 'text-danger' : 'text-success'"
                  >
                    {{ operationBalanceSupplierNotaDisplay(note) }}
                  </td>
                  <td class="text-end text-muted">-</td>
                  <td></td>
                </tr>
                </template>
                <!-- Costs Subtotal (only show if there are costs) -->
                <tr v-if="balanceData.costsSummary && balanceData.costsSummary.total > 0" class="">
                  <td class="fw-bold">
                    <i class="fas fa-calculator me-2"></i>
                    {{ $t("financialSummary.balance.costsSubtotal") }}
                  </td>
                  <td class="text-end fw-bold" :class="balanceData.costsSummary.total > 0 ? 'text-primary' : ''">
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas('__costs__', 'amount')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownAmountHint')"
                        @click.stop="openExpenseBreakdown('__costs__', 'amount', $t('financialSummary.balance.costsSubtotal'))"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ costsSubtotalDisplay.prefix }}{{ formatNumber(costsSubtotalDisplay.total) }}</span>
                    </span>
                  </td>
                  <template v-if="balanceFx">
                    <td></td>
                    <td class="text-end fw-bold tabular-nums text-nowrap">{{ costsSubtotalDisplay.converted ? costsSubtotalDisplay.prefix + formatNumber(costsSubtotalDisplay.total) : "—" }}</td>
                  </template>
                  <td 
                    class="text-end fw-bold"
                    :class="balanceData.costsSummary.paid > 0 ? 'text-success' : ''"
                    @click="(balanceData.costsSummary.paid > 0 || balanceData.costsSummary.balance > 0) ? viewPaymentDetails() : null"
                    :style="(balanceData.costsSummary.paid > 0 || balanceData.costsSummary.balance > 0) ? 'cursor: pointer;' : ''"
                    :title="(balanceData.costsSummary.paid > 0 || balanceData.costsSummary.balance > 0) ? $t('financialSummary.viewPayments') : ''"
                  >
                    <span class="d-inline-flex align-items-center justify-content-end gap-1">
                      <button
                        v-if="expenseBreakdownHas('__costs__', 'paid')"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-info"
                        :title="$t('financialSummary.balance.breakdownPaidHint')"
                        @click.stop="openExpenseBreakdown('__costs__', 'paid', $t('financialSummary.balance.costsSubtotal'))"
                      >
                        <i class="fas fa-eye" aria-hidden="true"></i>
                      </button>
                      <span>{{ costsSubtotalDisplay.prefix }}{{ formatNumber(costsSubtotalDisplay.paid) }}</span>
                    </span>
                  </td>
                  <td 
                    class="text-end fw-bold"
                    :class="balanceData.costsSummary.balance > 0 ? 'text-danger' : ''"
                    @click="balanceData.costsSummary.balance > 0 ? viewPaymentDetails() : null"
                    :style="balanceData.costsSummary.balance > 0 ? 'cursor: pointer;' : ''"
                    :title="balanceData.costsSummary.balance > 0 ? $t('financialSummary.viewPayments') : ''"
                  >
                    {{ costsSubtotalDisplay.prefix }}{{ formatNumber(costsSubtotalDisplay.balance) }}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Margins Summary (Duntower: hide Projected — ticket #304 / Sol) -->
          <div class="margins-summary mt-4 pt-3 border-top" v-if="balanceData.margins">
            <div class="row text-center">
              <!-- Projected Margin -->
              <div
                v-if="!isDuntowerTenant"
                class="col-md-4 col-6 mb-2"
              >
                <div class="total-item">
                  <small class="text-muted d-block">
                    {{ $t("financialSummary.balance.projectedMargin") }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 ms-1 align-baseline"
                      @click="showMarginHelp('projected')"
                      :title="$t('financialSummary.balance.marginHelpAria')"
                      :aria-label="$t('financialSummary.balance.marginHelpAria')"
                    >
                      <i class="fas fa-question-circle text-muted" aria-hidden="true"></i>
                    </button>
                  </small>
                  <strong class="fs-5" :class="balanceData.margins.projected >= 0 ? 'text-success' : 'text-danger'">
                    {{ marginMoneyPrefix }}{{ formatMarginAmount(balanceData.margins.projected) }}
                  </strong>
                  <small class="d-block" :class="balanceData.margins.projected >= 0 ? 'text-success' : 'text-danger'">
                    ({{ balanceData.margins.projected >= 0 ? '+' : '' }}{{ formatMarginPercent(balanceData.margins.projected, projectedMarginBase) }}%)
                  </small>
                  <small class="text-muted d-block">{{ $t("financialSummary.balance.basedOnProforma") }}</small>
                </div>
              </div>
              <!-- Real Margin -->
              <div
                class="mb-2"
                :class="isDuntowerTenant ? 'col-md-6 col-6' : 'col-md-4 col-6'"
              >
                <div class="total-item">
                  <small class="text-muted d-block">
                    {{ $t("financialSummary.balance.realMargin") }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 ms-1 align-baseline"
                      @click="showMarginHelp('real')"
                      :title="$t('financialSummary.balance.marginHelpAria')"
                      :aria-label="$t('financialSummary.balance.marginHelpAria')"
                    >
                      <i class="fas fa-question-circle text-muted" aria-hidden="true"></i>
                    </button>
                  </small>
                  <strong class="fs-5" :class="balanceData.margins.real >= 0 ? 'text-success' : 'text-danger'">
                    {{ marginMoneyPrefix }}{{ formatMarginAmount(balanceData.margins.real) }}
                  </strong>
                  <small class="d-block" :class="balanceData.margins.real >= 0 ? 'text-success' : 'text-danger'">
                    ({{ balanceData.margins.real >= 0 ? '+' : '' }}{{ formatMarginPercent(balanceData.margins.real, realMarginBase) }}%)
                  </small>
                  <small class="text-muted d-block">{{ $t("financialSummary.balance.basedOnAllRequests") }}</small>
                </div>
              </div>
              <!-- Current Margin -->
              <div
                class="mb-2"
                :class="isDuntowerTenant ? 'col-md-6 col-12' : 'col-md-4 col-12'"
              >
                <div class="total-item">
                  <small class="text-muted d-block">
                    {{ $t("financialSummary.balance.currentMargin") }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 ms-1 align-baseline"
                      @click="showMarginHelp('current')"
                      :title="$t('financialSummary.balance.marginHelpAria')"
                      :aria-label="$t('financialSummary.balance.marginHelpAria')"
                    >
                      <i class="fas fa-question-circle text-muted" aria-hidden="true"></i>
                    </button>
                  </small>
                  <strong class="fs-4" :class="balanceData.margins.current >= 0 ? 'text-success' : 'text-danger'">
                    {{ marginMoneyPrefix }}{{ formatMarginAmount(balanceData.margins.current) }}
                    <button 
                      class="btn btn-link btn-sm p-0 ms-1" 
                      @click="viewCashFlowDetails"
                      :title="$t('financialSummary.viewCashFlow')"
                    >
                      <i class="fas fa-eye text-info"></i>
                    </button>
                  </strong>
                  <small class="d-block" :class="balanceData.margins.current >= 0 ? 'text-success' : 'text-danger'">
                    ({{ balanceData.margins.current >= 0 ? '+' : '' }}{{ formatMarginPercent(balanceData.margins.current, currentMarginBase) }}%)
                  </small>
                  <small class="text-muted d-block">{{ $t("financialSummary.balance.basedOnPaidOnly") }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Shipping Details (only for proformas with shippings) -->
          
        </div>

        <!-- No data state -->
        <div v-else class="text-muted text-center py-3">
          <i class="fas fa-info-circle fa-2x mb-2 d-block text-muted"></i>
          {{ $t("financialSummary.balance.noData") }}
        </div>
      </div>
    </div>

    <!-- Tipo de cambio de una moneda de la operación (exchange_rates_module, #397) -->
    <OperationExchangeRateModal
      :visible="!!fxModalEntry"
      ref-model="shipping"
      :ref-id="fxModalRefId"
      :entry="fxModalEntry"
      :currency-label="fxModalEntry ? fxCurrencyLabel(fxModalEntry.currencyId) : ''"
      :reporting-label="fxReportingLabel"
      @close="closeFxModal"
      @saved="onFxSaved"
    />

    <!-- Add Collection Modal (simplified: bank account, comment, file) -->
    <NotaComercialNoteModal
      ref="notaComercialNoteModal"
      :lists="lists"
      @saved="handleNotaComercialNoteSaved"
    />

    <QuickAddCollectModal
      v-if="showAddCollectionModal"
      :isVisible="showAddCollectionModal"
      :lists="lists"
      :parentID="parentID"
      :parentModel="parentModel"
      :extraParams="extraParams"
      :allow-amount-edit="quickCollectAllowAmountEdit"
      :amount-max="quickCollectAmountMax"
      :stage1-auto-ship-advance-split="quickCollectStage1AutoShipSplitContext"
      :show-container-amount-calculator="showCollectContainerCalculator"
      :prefill-source-item="showCollectContainerCalculator ? itemData : null"
      :prefill-source-model="showCollectContainerCalculator ? modelName || 'Proform' : ''"
      :quick-collect-advance-pct="
        showCollectContainerCalculator &&
        collectionStagesVisual &&
        collectionStagesVisual.stage1
          ? Number(collectionStagesVisual.stage1.advancePct)
          : null
      "
      @close="closeAddCollectionModal"
      @saved="handleCollectionSaved"
    />

    <!-- Collection Details Modal -->
    <div 
      v-if="showCollectionDetailsModal" 
      class="modal fade show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="closeCollectionDetailsModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-list me-2"></i>
              {{ $t("financialSummary.collectionDetails") }}
            </h5>
            <button type="button" class="btn-close" @click="closeCollectionDetailsModal"></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="loadingCollectionDetails" variant="table" :rows="4" :cols="4" padded />
            <div v-else-if="collectionDetails">
              <!-- Summary -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.expectedAmount") }}</small>
                      <h5 class="mb-0 text-primary">{{ moneyPrefix }}{{ formatNumber(collectionDetails.expectedAmount) }}</h5>
                      <div
                        v-if="
                          collectionDetails.targetItem &&
                            Math.abs(Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) > 0.01
                        "
                        class="mt-2 small rounded border overflow-hidden"
                        role="note"
                      >
                        <div
                          class="px-2 py-1 text-uppercase fw-semibold border-bottom bg-light text-secondary"
                          style="font-size: 0.58rem; letter-spacing: 0.04em"
                        >
                          <i class="fas fa-file-invoice me-1" aria-hidden="true"></i>
                          {{ $t("financialSummary.stages.notaImpactBoxCaption") }}
                        </div>
                        <div
                          class="px-2 py-2 fw-semibold tabular-nums"
                          :class="
                            (Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) >= 0
                              ? 'text-success border-start border-success border-3'
                              : 'text-danger border-start border-danger border-3'
                          "
                        >
                          {{
                            $t("financialSummary.stages.notaImpactExpectedNet", {
                              amount:
                                (Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0) >= 0
                                  ? "-" + moneyPrefix + formatNumber(collectionDetails.targetItem.point1CreditNotesGiven)
                                  : "+" + moneyPrefix + formatNumber(
                                      Math.abs(Number(collectionDetails.targetItem.point1CreditNotesGiven) || 0)
                                    ),
                            })
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.collectedAmount") }}</small>
                      <h5 class="mb-0 text-success">
                        {{ moneyPrefix }}{{ formatNumber(collectionDetails.collectedTotal !== undefined ? collectionDetails.collectedTotal : collectionDetails.collectedAmount) }}
                      </h5>
                      <small
                        v-if="collectionDetails.targetItem && collectionDetails.targetItem.type === 'shipping'"
                        class="text-muted d-block"
                      >
                        {{ $t("financialSummary.shippingCollectionsApplied") }}: {{ moneyPrefix }}{{ formatNumber(collectionDetails.collectedAmount || 0) }}
                        <span v-if="(collectionDetails.targetItem.proformAdvanceDeducted || 0) > 0">
                          | {{ $t("financialSummary.advanceDeducted") }}: {{ moneyPrefix }}{{ formatNumber(collectionDetails.targetItem.proformAdvanceDeducted || 0) }}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Collections Table -->
              <div v-if="collectionDetails.collections && collectionDetails.collections.length > 0">
                <h6 class="mb-2">
                  <i class="fas fa-receipt me-2"></i>
                  {{ $t("financialSummary.collections") }} ({{ collectionDetails.collections.length }})
                </h6>
                <div class="table-responsive">
                  <table class="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>{{ $t("financialSummary.name") }}</th>
                        <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                        <th>{{ $t("financialSummary.status") }}</th>
                        <th>{{ $t("financialSummary.date") }}</th>
                        <th>{{ $t("financialSummary.actionsColumn") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="collection in collectionDetails.collections" :key="collection._id">
                        <td>{{ collection.name || collection.idModel }}</td>
                        <td class="text-end fw-bold text-success">
                          {{ moneyPrefix }}{{ formatNumber(collection.appliedAmount !== undefined ? collection.appliedAmount : collection.amount) }}
                        </td>
                        <td>
                          <span 
                            class="badge" 
                            :class="collection.received ? 'bg-success' : 'bg-warning'"
                          >
                            {{ collection.received ? $t("financialSummary.received") : $t("financialSummary.pending") }}
                          </span>
                        </td>
                        <td>{{ formatDate(collection.collection_date || collection.createdAt) }}</td>
                        <td>
                          <div class="btn-group btn-group-sm">
                            <button 
                              class="btn btn-outline-success" 
                              @click="openReceiptModal(collection._id)"
                              :title="$t('financialSummary.viewReceipt')"
                            >
                              <i class="fas fa-receipt"></i>
                            </button>
                            <button 
                              class="btn btn-outline-primary" 
                              @click="goToCollect(collection._id)"
                              :title="$t('financialSummary.viewDetails')"
                            >
                              <i class="fas fa-external-link-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-3">
                <i class="fas fa-inbox fa-2x text-muted mb-2"></i>
                <p class="text-muted mb-0">{{ $t("financialSummary.noCollections") }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCollectionDetailsModal">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Details Modal -->
    <div 
      v-if="showPaymentDetailsModal" 
      class="modal fade show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="closePaymentDetailsModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-list me-2"></i>
              {{ $t("financialSummary.paymentDetails") }}
            </h5>
            <button type="button" class="btn-close" @click="closePaymentDetailsModal"></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="loadingPaymentDetails" variant="table" :rows="4" :cols="4" padded />
            <div v-else-if="paymentDetails">
              <!-- Summary -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.payments.totalToPay") }}</small>
                      <h5 class="mb-0 text-primary">{{ moneyPrefix }}{{ formatNumber(paymentDetails.totalToPay || 0) }}</h5>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card bg-light">
                    <div class="card-body py-2">
                      <small class="text-muted">{{ $t("financialSummary.payments.totalPaid") }}</small>
                      <h5 class="mb-0 text-success">{{ moneyPrefix }}{{ formatNumber(paymentDetails.totalPaid || 0) }}</h5>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payments Table -->
              <div v-if="paymentDetails.payments && paymentDetails.payments.length > 0">
                <h6 class="mb-2">
                  <i class="fas fa-arrow-up me-2"></i>
                  {{ $t("financialSummary.payments.title") }} ({{ paymentDetails.payments.length }})
                </h6>
                <div class="table-responsive">
                  <table class="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>{{ $t("financialSummary.name") }}</th>
                        <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                        <th>{{ $t("financialSummary.status") }}</th>
                        <th>{{ $t("financialSummary.date") }}</th>
                        <th>{{ $t("financialSummary.actionsColumn") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payment in paymentDetails.payments" :key="payment._id">
                        <td>{{ payment.name || 'PAY-' + payment.idModel }}</td>
                        <td class="text-end fw-bold text-danger">{{ moneyPrefix }}{{ formatNumber(payment.appliedAmount != null ? payment.appliedAmount : payment.amount) }}</td>
                        <td>
                          <span 
                            class="badge" 
                            :class="payment.paid ? 'bg-success' : 'bg-warning'"
                          >
                            {{ payment.paid ? $t("financialSummary.payments.paid") : $t("financialSummary.payments.pending") }}
                          </span>
                        </td>
                        <td>{{ formatDate(payment.payment_date || payment.date || payment.createdAt) }}</td>
                        <td>
                          <div class="btn-group btn-group-sm">
                            <button 
                              class="btn btn-outline-primary" 
                              @click="goToPayment(payment.paymentId || payment._id)"
                              :title="$t('financialSummary.viewDetails')"
                            >
                              <i class="fas fa-external-link-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-3">
                <i class="fas fa-inbox fa-2x text-muted mb-2"></i>
                <p class="text-muted mb-0">{{ $t("financialSummary.payments.noPayments") }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePaymentDetailsModal">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Desglose de una fila del balance: de qué gastos sale el monto / con qué pagos se cubre -->
    <div
      v-if="expenseBreakdown"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="closeExpenseBreakdown"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-eye me-2"></i>
              {{
                expenseBreakdown.mode === "paid"
                  ? $t("financialSummary.balance.breakdownPaidTitle", { concept: expenseBreakdown.title })
                  : $t("financialSummary.balance.breakdownAmountTitle", { concept: expenseBreakdown.title })
              }}
            </h5>
            <button type="button" class="btn-close" @click="closeExpenseBreakdown"></button>
          </div>
          <div class="modal-body">
            <!-- Monto: de qué gastos sale -->
            <div v-if="expenseBreakdown.mode !== 'paid'" class="table-responsive">
              <table class="table table-sm table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th>{{ $t("financialSummary.balance.concept") }}</th>
                    <th>{{ $t("financialSummary.balance.breakdownContact") }}</th>
                    <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                    <th class="text-end">{{ $t("financialSummary.balance.paid") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in expenseBreakdown.rows" :key="row.key">
                    <td>
                      <div>{{ row.label }}</div>
                      <div v-if="row.detail" class="small text-muted">{{ row.detail }}</div>
                      <div v-if="row.shippingName" class="small text-muted">{{ row.shippingName }}</div>
                    </td>
                    <td>{{ row.contactName || "-" }}</td>
                    <td class="text-end text-primary">{{ currencyPrefixFor(row.currencyId) }}{{ formatNumber(row.amount) }}</td>
                    <td class="text-end" :class="row.paid > 0 ? 'text-success' : 'text-muted'">
                      {{ currencyPrefixFor(row.currencyId) }}{{ formatNumber(row.paid) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="fw-bold">
                    <td colspan="2">{{ $t("financialSummary.balance.totalAmount") }}</td>
                    <td class="text-end text-primary">{{ expenseBreakdownTotalDisplay("amount") }}</td>
                    <td class="text-end text-success">{{ expenseBreakdownTotalDisplay("paid") }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Pagado: con qué pagos se cubre -->
            <div v-else>
              <div v-if="expenseBreakdownPaymentRows().length" class="table-responsive">
                <table class="table table-sm table-striped align-middle mb-0">
                  <thead>
                    <tr>
                      <th>{{ $t("financialSummary.balance.concept") }}</th>
                      <th>{{ $t("financialSummary.name") }}</th>
                      <th>{{ $t("financialSummary.status") }}</th>
                      <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                      <th class="text-center">{{ $t("financialSummary.actionsColumn") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in expenseBreakdownPaymentRows()" :key="row.key">
                      <td>
                        <div>{{ row.label }}</div>
                        <div v-if="row.detail" class="small text-muted">{{ row.detail }}</div>
                        <div v-if="row.shippingName" class="small text-muted">{{ row.shippingName }}</div>
                      </td>
                      <td>
                        <div>{{ row.payment.name || "-" }}</div>
                        <div v-if="expenseBreakdownPaymentContact(row.payment)" class="small text-muted">
                          {{ expenseBreakdownPaymentContact(row.payment) }}
                        </div>
                      </td>
                      <td>
                        <span class="badge" :class="row.payment.paid ? 'bg-success' : 'bg-warning'">
                          {{
                            row.payment.paid
                              ? $t("financialSummary.payments.paid")
                              : $t("financialSummary.payments.pending")
                          }}
                        </span>
                        <span
                          v-if="row.payment.timesApplied > 1"
                          class="badge bg-danger ms-1"
                          :title="$t('financialSummary.stages.shipPaymentsPillDuplicatedBadgeHint', { times: row.payment.timesApplied })"
                        >×{{ row.payment.timesApplied }}</span>
                      </td>
                      <td class="text-end" :class="row.payment.paid ? 'text-success' : 'text-danger'">
                        {{ currencyPrefixFor(row.currencyId) }}{{ formatNumber(expenseBreakdownPaymentAmount(row.payment)) }}
                      </td>
                      <td class="text-center">
                        <button
                          class="btn btn-outline-primary btn-sm"
                          :title="$t('financialSummary.viewDetails')"
                          @click="goToPayment(row.payment._id)"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="fw-bold">
                      <td colspan="3">{{ $t("financialSummary.balance.paid") }}</td>
                      <td class="text-end text-success">{{ expenseBreakdownTotalDisplay("paid") }}</td>
                      <td></td>
                    </tr>
                    <tr v-if="expenseBreakdownTotal('requested') > 0.005" class="fw-bold">
                      <td colspan="3">{{ $t("financialSummary.balance.breakdownRequested") }}</td>
                      <td class="text-end text-warning">{{ expenseBreakdownTotalDisplay("requested") }}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div v-else class="text-center py-3">
                <i class="fas fa-inbox fa-2x text-muted mb-2"></i>
                <p class="text-muted mb-0">{{ $t("financialSummary.payments.noPayments") }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeExpenseBreakdown">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cash Flow Details Modal (Collections + Payments) -->
    <div 
      v-if="showCashFlowModal" 
      class="modal fade show d-block" 
      tabindex="-1" 
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="closeCashFlowModal"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-exchange-alt me-2"></i>
              {{ $t("financialSummary.cashFlowDetails") }}
            </h5>
            <button type="button" class="btn-close" @click="closeCashFlowModal"></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="loadingCashFlow" variant="table" :rows="5" :cols="3" padded />
            <div v-else-if="cashFlowData" class="row">
              <!-- Collections (Income) -->
              <div class="col-md-6">
                <div class="card border-success mb-3">
                  <div class="card-header bg-success bg-opacity-10">
                    <h6 class="mb-0 text-success">
                      <i class="fas fa-arrow-down me-2"></i>
                      {{ $t("financialSummary.collectionsIncome") }}
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3 text-center">
                      <small class="text-muted d-block">{{ $t("financialSummary.totalCollected") }}</small>
                      <h4 class="text-success mb-0">{{ moneyPrefix }}{{ formatNumber(cashFlowData.totalCollected) }}</h4>
                      <small v-if="fxUsdHint(fxClientEntry, cashFlowData.totalCollected)" class="d-block text-muted" style="font-size: 0.72rem">{{ fxUsdHint(fxClientEntry, cashFlowData.totalCollected) }}</small>
                    </div>
                    <div v-if="cashFlowData.collections && cashFlowData.collections.length > 0" class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                      <table class="table table-sm table-striped mb-0">
                        <thead class="sticky-top bg-white">
                          <tr>
                            <th>{{ $t("financialSummary.name") }}</th>
                            <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                            <th>{{ $t("financialSummary.date") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="collection in cashFlowData.collections" :key="collection._id">
                            <td>
                              <small>
                                <a 
                                  href="#" 
                                  @click.prevent="goToCollect(collection._id)"
                                  class="text-decoration-none"
                                  :title="$t('financialSummary.viewDetails')"
                                >
                                  {{ collection.name || collection.idModel }}
                                  <i class="fas fa-external-link-alt ms-1" style="font-size: 0.7rem;"></i>
                                </a>
                              </small>
                            </td>
                            <td class="text-end text-success fw-bold">{{ moneyPrefix }}{{ formatNumber(collection.amount) }}</td>
                            <td><small>{{ formatDate(collection.collection_date || collection.createdAt) }}</small></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="text-center py-3 text-muted">
                      <i class="fas fa-inbox"></i> {{ $t("financialSummary.noCollections") }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payments (Expenses) -->
              <div class="col-md-6">
                <div class="card border-danger mb-3">
                  <div class="card-header bg-danger bg-opacity-10">
                    <h6 class="mb-0 text-danger">
                      <i class="fas fa-arrow-up me-2"></i>
                      {{ $t("financialSummary.paymentsExpenses") }}
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3 text-center">
                      <small class="text-muted d-block">{{ $t("financialSummary.totalPaid") }}</small>
                      <h4 class="text-danger mb-0">{{ cashFlowPaidDisplay }}</h4>
                    </div>
                    <div v-if="cashFlowData.payments && cashFlowData.payments.length > 0" class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                      <table class="table table-sm table-striped mb-0">
                        <thead class="sticky-top bg-white">
                          <tr>
                            <th>{{ $t("financialSummary.name") }}</th>
                            <th class="text-end">{{ $t("financialSummary.amount") }}</th>
                            <th>{{ $t("financialSummary.date") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="payment in cashFlowData.payments" :key="payment._id">
                            <td>
                              <small>
                                <a 
                                  href="#" 
                                  @click.prevent="goToPayment(payment.paymentId || payment._id)"
                                  class="text-decoration-none"
                                  :title="$t('financialSummary.viewDetails')"
                                >
                                  {{ payment.name || 'PAY-' + payment.idModel }}
                                  <i class="fas fa-external-link-alt ms-1" style="font-size: 0.7rem;"></i>
                                </a>
                              </small>
                            </td>
                            <td class="text-end text-danger fw-bold">{{ currencyPrefixFor(payment.currencyId) }}{{ formatNumber(payment.appliedAmount != null ? payment.appliedAmount : payment.amount) }}</td>
                            <td><small>{{ formatDate(payment.payment_date || payment.createdAt) }}</small></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="text-center py-3 text-muted">
                      <i class="fas fa-inbox"></i> {{ $t("financialSummary.noPayments") }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Summary -->
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-body py-2">
                    <div class="row text-center">
                      <div class="col-4">
                        <small class="text-muted">{{ $t("financialSummary.totalCollected") }}</small>
                        <template v-if="cashFlowData.marginFigures">
                          <h5 class="text-success mb-0">+{{ cashFlowMarginPrefix }}{{ formatMarginAmount(cashFlowData.marginFigures.collected) }}</h5>
                          <small v-if="cashFlowCollectedOriginal" class="d-block text-muted" style="font-size: 0.72rem">({{ cashFlowCollectedOriginal }})</small>
                        </template>
                        <h5 v-else class="text-success mb-0">+{{ moneyPrefix }}{{ formatNumber(cashFlowData.totalCollected) }}</h5>
                      </div>
                      <div class="col-4">
                        <small class="text-muted">{{ $t("financialSummary.totalPaid") }}</small>
                        <h5 v-if="cashFlowData.marginFigures" class="text-danger mb-0">-{{ cashFlowMarginPrefix }}{{ formatMarginAmount(cashFlowData.marginFigures.paid) }}</h5>
                        <h5 v-else class="text-danger mb-0">-{{ moneyPrefix }}{{ formatNumber(cashFlowData.totalPaid) }}</h5>
                      </div>
                      <div class="col-4">
                        <small class="text-muted">{{ $t("financialSummary.balance.currentMargin") }}</small>
                        <h5 :class="cashFlowData.currentMargin >= 0 ? 'text-success' : 'text-danger'" class="mb-0">
                          <template v-if="cashFlowData.marginFigures">{{ cashFlowMarginPrefix }}{{ formatMarginAmount(cashFlowData.currentMargin) }}</template>
                          <template v-else>{{ moneyPrefix }}{{ formatNumber(cashFlowData.currentMargin) }}</template>
                        </h5>
                        <!-- El %, como en la tarjeta del margen (#505 duntower) -->
                        <small
                          v-if="Number.isFinite(Number(cashFlowData.currentMargin))"
                          :class="cashFlowData.currentMargin >= 0 ? 'text-success' : 'text-danger'"
                        >({{ cashFlowData.currentMargin >= 0 ? '+' : '' }}{{ formatMarginPercent(cashFlowData.currentMargin, currentMarginBase) }}%)</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCashFlowModal">
              {{ $t("close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Payment Modal -->
    <ReferenceModal
      v-if="showAddPaymentModal"
      :isVisible="showAddPaymentModal"
      :modelName="'payment'"
      :modelLabel="'Payment'"
      :lists="lists"
      :models="[]"
      :visualGroups="[]"
      :parentID="parentID"
      :selectedModel="parentModel"
      :extraParams="extraParams"
      @close="closeAddPaymentModal"
      @saved="handlePaymentSaved"
    />

    <QuickEditShippingExpenseModal
      v-if="showQuickEditShippingExpenseModal"
      :is-visible="showQuickEditShippingExpenseModal"
      :expense-id="quickEditShippingExpenseId"
      @close="closeQuickEditShippingExpenseModal"
      @saved="handleQuickEditShippingExpenseSaved"
    />

    <QuickRequestPaymentModal
      v-if="showQuickRequestPaymentModal"
      :isVisible="showQuickRequestPaymentModal"
      :expense-id="quickRequestPaymentExpenseId"
      :expense-name="quickRequestPaymentExpenseName"
      :amount-max="quickRequestPaymentAmountMax"
      :amount-suggested="quickRequestPaymentAmountSuggested"
      :split-supplier-mode="quickRequestPaymentSplitSupplierMode"
      :split-shipping-id="quickRequestPaymentSplitShippingId"
      :lists="lists"
      :extra-params="quickRequestPaymentExtraParams"
      @close="closeQuickRequestPaymentModal"
      @saved="handleQuickRequestPaymentSaved"
    />

    <PaymentMarkPaidModal
      ref="paymentMarkPaidModal"
      @saved="onPaymentMarkPaidModalSaved"
    />

    <QuickMarkPaymentPaidModal
      ref="quickMarkPaymentPaidModal"
      @saved="onPaymentMarkPaidModalSaved"
    />
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ReferenceModal from "@/components/gotrading/common/ReferenceModal.vue";
import QuickAddCollectModal from "@/components/gotrading/common/QuickAddCollectModal.vue";
import QuickEditShippingExpenseModal from "@/components/gotrading/common/QuickEditShippingExpenseModal.vue";
import QuickRequestPaymentModal from "@/components/gotrading/common/QuickRequestPaymentModal.vue";
import PaymentMarkPaidModal from "@/components/financial/PaymentMarkPaidModal.vue";
import QuickMarkPaymentPaidModal from "@/components/financial/QuickMarkPaymentPaidModal.vue";
import { notaComercialModalContextFromExpenseLine } from "@/modules/nota_comercial_module/utils/notaComercialFromExpenseLine";
import NotaComercialPendingBadge from "@/modules/nota_comercial_module/components/NotaComercialPendingBadge.vue";
import NotaComercialAssignButton from "@/modules/nota_comercial_module/components/NotaComercialAssignButton.vue";
import notaComercialUnapplyMixin from "@/modules/nota_comercial_module/mixins/notaComercialUnapplyMixin";
import CollectionSummary from "@/components/gotrading/CollectionSummary.vue";
import NotaComercialNoteModal from "@/modules/nota_comercial_module/components/NotaComercialNoteModal.vue";
import GenerateAllEntriesButton from "@/components/gotrading/accounting/GenerateAllEntriesButton.vue";
import permissionsMixin from "@/mixins/permissionsMixin";
import operationCurrencyMixin from "./mixins/operationCurrencyMixin.js";
import OperationExchangeRateModal from "@/modules/exchange_rates_module/components/OperationExchangeRateModal.vue";
import OperationExchangeRateCell from "@/modules/exchange_rates_module/components/OperationExchangeRateCell.vue";
import { formatNumberSync } from "@/utils/numberFormatter";
import { buildStage1AutoShipSplitContext } from "@/components/gotrading/utils/stage1CollectAmountSplit.js";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";
import { formatDateSync } from "@/utils/dateFormatter";
import {
  canDeletePaymentsFor,
  confirmAndDeletePayment,
} from "@/utils/paymentDeleteFlow";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { loadNegotiationExpensesForShipping } from "@/modules/fobtrading_module/utils/negotiationExpensesFlow";

/** Id de un campo de referencia, venga poblado o como id suelto. */
function ncContactId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const id = value._id || value.id;
    return id != null ? String(id) : "";
  }
  return String(value);
}

export default {
  name: "FinancialSummary",
  mixins: [permissionsMixin, notaComercialUnapplyMixin, operationCurrencyMixin],
  components: {
    ContentSkeleton,
    NotaComercialPendingBadge,
    NotaComercialAssignButton,
    ReferenceModal,
    QuickAddCollectModal,
    QuickEditShippingExpenseModal,
    QuickRequestPaymentModal,
    PaymentMarkPaidModal,
    QuickMarkPaymentPaidModal,
    CollectionSummary,
    GenerateAllEntriesButton,
    NotaComercialNoteModal,
    OperationExchangeRateModal,
    OperationExchangeRateCell,
  },
  props: {
    modelName: {
      type: String,
      required: true,
    },
    itemData: {
      type: Object,
      required: true,
    },
    /** Si es false, no se muestra CollectionSummary (etapas de cobro en proforma); p. ej. módulo FOB Trading desactivado. */
    fobtradingModuleActive: {
      type: Boolean,
      default: true,
    },
    /** Módulo nota_comercial_module activo en el tenant. */
    notaComercialModuleActive: {
      type: Boolean,
      default: false,
    },
    /** Modo compacto: renderiza solo CollectionSummary (sin tarjetas extras de resumen/pagos). */
    collectionSummaryOnly: {
      type: Boolean,
      default: false,
    },
    /** Solo la tarjeta «Operation Balance» (pestaña propia en /operacion). */
    balanceOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadingPayments: false,
      loadingNegotiationExpenses: false,
      loadingBalance: false,
      financialData: null,
      paymentData: null,
      balanceData: null,
      summaryMessages: [],
      paymentMessages: [],
      // Add collection modal
      showAddCollectionModal: false,
      showCollectContainerCalculator: false,
      showAddPaymentModal: false,
      showQuickEditShippingExpenseModal: false,
      quickEditShippingExpenseId: null,
      showQuickRequestPaymentModal: false,
      quickRequestPaymentExpenseId: null,
      quickRequestPaymentExpenseName: "",
      quickRequestPaymentAmountMax: null,
      quickRequestPaymentAmountSuggested: null,
      quickRequestPaymentSplitSupplierMode: false,
      quickRequestPaymentSplitShippingId: null,
      quickRequestPaymentExtraParams: {},
      // View collection details modal
      showCollectionDetailsModal: false,
      loadingCollectionDetails: false,
      collectionDetails: null,
      // View payment details modal
      showPaymentDetailsModal: false,
      loadingPaymentDetails: false,
      paymentDetails: null,
      /** Desglose de una fila del balance: de qué gastos sale el monto y con qué pagos se cubre. */
      expenseBreakdown: null,
      /** Moneda cuyo tipo de cambio se está viendo en el modal (#397). */
      fxModalCurrencyId: null,
      /** En la PI: embarque cuyo tipo de cambio se edita en el modal. */
      fxModalShippingId: null,
      // Cash flow details modal (collections + payments)
      showCashFlowModal: false,
      loadingCashFlow: false,
      cashFlowData: null,
      lists: [],
      parentID: null,
      parentModel: null,
      extraParams: {},
      /** Quick collect: permitir editar monto (etapa 1 proforma con saldo pendiente). */
      quickCollectAllowAmountEdit: false,
      quickCollectAmountMax: null,
    };
  },
  computed: {
    /**
     * Tipo de cambio de la operación (exchange_rates_module, #397): el balance del embarque
     * trae uno por moneda (`rates`) y el de la PI uno por embarque (`byShipping`), y cada
     * fila se muestra también en la moneda del sistema.
     */
    balanceFx() {
      const fx = this.balanceData ? this.balanceData.fx : null;
      return fx && fx.reportingCurrencyId ? fx : null;
    },
    balanceColspan() {
      return this.balanceFx ? 7 : 5;
    },
    fxReportingLabel() {
      return this.balanceFx ? this.fxCurrencyLabel(this.balanceFx.reportingCurrencyId) || "USD" : "USD";
    },
    fxReportingPrefix() {
      return this.balanceFx ? this.currencyPrefixFor(this.balanceFx.reportingCurrencyId) : this.moneyPrefix;
    },
    fxModalEntry() {
      return this.fxModalCurrencyId && this.balanceFx
        ? this.fxEntryFor(this.fxModalCurrencyId, this.fxModalShippingId)
        : null;
    },
    /** Operación del modal: el embarque; en la PI, el embarque de la fila. */
    fxModalRefId() {
      if (this.balanceData && this.balanceData.isProform) return this.fxModalShippingId || "";
      return this.itemData && this.itemData._id ? String(this.itemData._id) : "";
    },
    /** Partes de la fila Supplier Invoice: en la PI, una por embarque con su moneda. */
    fxSupplierParts() {
      const bd = this.balanceData;
      if (!bd || !bd.supplier) return [];
      if (bd.isProform) {
        return (bd.shippings || []).map((ship) => ({
          shippingId: ship._id,
          currencyId: ship.supplierCurrencyId,
          amount: this.shipSupplierDue(ship),
        }));
      }
      return [{ shippingId: null, currencyId: bd.supplier.currencyId, amount: this.balanceSupplierExpenseDueDisplay }];
    },
    /** Partes de la fila de la Invoice (Total Invoices en la PI). */
    fxClientInvoiceParts() {
      const bd = this.balanceData;
      if (!bd || !bd.client) return [];
      if (bd.isProform) return (bd.shippings || []).map((ship) => this.fxShippingInvoiceParts(ship)[0]);
      return [{ shippingId: null, currencyId: bd.client.currencyId, amount: bd.client.totalInvoice }];
    },
    /** Moneda del margen en el detalle de caja (USD con tipo de cambio de operación). */
    cashFlowMarginPrefix() {
      const mf = this.cashFlowData && this.cashFlowData.marginFigures;
      return mf && mf.currencyId ? this.currencyPrefixFor(mf.currencyId) : this.marginMoneyPrefix;
    },
    /** Lo cobrado en la moneda de la Invoice, al lado del convertido del resumen. */
    cashFlowCollectedOriginal() {
      const mf = this.cashFlowData && this.cashFlowData.marginFigures;
      const o = mf && mf.original;
      if (!o || !Number.isFinite(Number(o.collected))) return "";
      return this.currencyPrefixFor(o.currencyId) + this.formatMarginAmount(o.collected);
    },
    /**
     * Total pagado del detalle de caja: en la moneda de los gastos si son todos de una; si
     * mezclan, en la del margen (#397), sumarlos crudos no da un número.
     */
    cashFlowPaidDisplay() {
      const cf = this.cashFlowData;
      if (!cf) return "";
      const ids = [...new Set((cf.payments || []).map((p) => String(p.currencyId || this.operationCurrencyId || "")))];
      if (ids.length <= 1) return this.currencyPrefixFor(ids[0] || null) + this.formatNumber(cf.totalPaid);
      if (cf.marginFigures) return this.cashFlowMarginPrefix + this.formatNumber(cf.marginFigures.paid);
      return this.moneyPrefix + this.formatNumber(cf.totalPaid);
    },
    /** Tipo de cambio de la Invoice (en la PI, el de sus embarques si coincide). */
    fxClientEntry() {
      return this.fxEntryForParts(this.fxClientInvoiceParts);
    },
    /** La factura del proveedor puede estar en otra moneda que la del cliente. */
    supplierMoneyPrefix() {
      const s = this.balanceData && this.balanceData.supplier;
      return s && s.currencyId ? this.currencyPrefixFor(s.currencyId) : this.moneyPrefix;
    },
    /**
     * Subtotal de costos: con tipo de cambio de operación, en la moneda del sistema (sumar
     * gastos de monedas distintas sin convertir no da un número). Si falta alguna
     * cotización, la suma de siempre.
     */
    costsSubtotalDisplay() {
      const summary = (this.balanceData && this.balanceData.costsSummary) || {};
      const raw = {
        prefix: this.moneyPrefix,
        total: summary.total,
        paid: summary.paid,
        balance: summary.balance,
        converted: false,
      };
      // En la PI, cada gasto de cada embarque con el tipo de cambio de su embarque.
      const costs =
        this.balanceData && this.balanceData.isProform
          ? (this.balanceData.shippings || []).flatMap((ship) =>
              (ship.costs || []).map((cost) => ({ ...cost, shippingId: ship._id }))
            )
          : (this.balanceData && this.balanceData.costs) || [];
      if (!this.balanceFx || !costs.length) return raw;
      const totals = { total: 0, paid: 0, balance: 0 };
      for (const cost of costs) {
        const rate = this.fxEntryFor(cost.currencyId, cost.shippingId).rate;
        if (!rate) return raw;
        const effective = cost.amountEffective != null ? cost.amountEffective : cost.amount;
        totals.total += (Number(effective) || 0) * rate;
        totals.paid += (Number(cost.paid) || 0) * rate;
        totals.balance += (Number(cost.balance) || 0) * rate;
      }
      return { prefix: this.fxReportingPrefix, ...totals, converted: true };
    },
    /**
     * Base del porcentaje del margen real: la factura ya ajustada por las notas comerciales,
     * que es el ingreso contra el que se compara. Sin el dato (respuesta vieja) cae a la factura.
     */
    /** Base del porcentaje del proyectado: la proforma ya ajustada por las notas comerciales. */
    projectedMarginBase() {
      const m = this.balanceData && this.balanceData.margins ? this.balanceData.margins : {};
      const net = Number(m.proformTotalNet);
      return Number.isFinite(net) ? net : m.proformTotal;
    },
    /**
     * Base del porcentaje del margen actual: lo cobrado, que es el ingreso con el que se
     * calculó. Con la factura como base, una operación cerrada mostraba tres porcentajes
     * distintos para el mismo resultado.
     */
    currentMarginBase() {
      const m = this.balanceData && this.balanceData.margins ? this.balanceData.margins : {};
      const cashIn = Number(m.explanation && m.explanation.cashIn);
      if (Number.isFinite(cashIn) && Math.abs(cashIn) > 0.009) return cashIn;
      const net = Number(m.shippingTotalNet);
      return Number.isFinite(net) ? net : m.shippingTotal;
    },
    realMarginBase() {
      const m = this.balanceData && this.balanceData.margins ? this.balanceData.margins : {};
      const net = Number(m.shippingTotalNet);
      return Number.isFinite(net) ? net : m.shippingTotal;
    },
    isDuntowerTenant() {
      return isDuntowerTenant();
    },
    /** Destino de las notas en esta pantalla: la proforma o el embarque que se está viendo. */
    ncOperationTargetType() {
      return this.isProform ? "proform" : "shipping";
    },
    ncOperationTargetId() {
      return this.itemData && this.itemData._id ? String(this.itemData._id) : "";
    },
    isProform() {
      return this.modelName?.toLowerCase() === "proform";
    },
    isShipping() {
      return this.modelName?.toLowerCase() === "shipping";
    },
    /**
     * Proveedor: factura tal como la emitió el proveedor cuando sus NC/ND se listan como subfilas
     * (el pendiente ya viene neteado). Sin notas para desglosar se mantiene el neto del API.
     */
    balanceSupplierExpenseDueDisplay() {
      const s = this.balanceData && this.balanceData.supplier;
      if (!s) return 0;
      if (this.operationBalanceSupplierNotes.length) return Number(s.invoice) || 0;
      const eff = s.invoiceEffective;
      if (eff != null && eff !== "" && Number.isFinite(Number(eff))) return Number(eff);
      return Number(s.invoice) || 0;
    },
    operationBalanceProformCreditNotes() {
      const notes = this.balanceData && this.balanceData.client && this.balanceData.client.clientProformCreditNotes;
      return Array.isArray(notes) ? notes : [];
    },
    /** Notas aplicadas al shipping actual (vista Finance de un embarque, no proforma). */
    operationBalanceShippingCreditNotes() {
      if (!this.balanceData || this.balanceData.isProform) return [];
      const fromClient =
        this.balanceData.client && this.balanceData.client.clientCreditNotes;
      if (Array.isArray(fromClient) && fromClient.length) return fromClient;
      const top = this.balanceData.clientCreditNotes;
      return Array.isArray(top) ? top : [];
    },
    /** Notas comerciales del lado proveedor aplicadas a la operacion (seccion SUPPLIER). */
    operationBalanceAllSupplierNotes() {
      const notes =
        this.balanceData && this.balanceData.supplier && this.balanceData.supplier.notes;
      return Array.isArray(notes) ? notes : [];
    },
    /** Roles que ya tienen su propia fila de costo debajo. */
    operationBalanceCostRoleKeys() {
      const rows = (this.balanceData && this.balanceData.costs) || [];
      return new Set(
        rows
          .map((cost) => String((cost && cost.notaRoleKey) || "").trim())
          .filter(Boolean)
      );
    },
    /**
     * Las que cuelgan de la factura del proveedor: las del rol «Supplier» y las que no
     * encuentran fila propia (sin rol, o de un gasto que este embarque no tiene), para
     * que ninguna se pierda de la vista. Una nota del courier es deuda de otro contacto
     * y va en su propia fila: sin filtrar aparecía acá aunque moviera aquel importe.
     */
    operationBalanceSupplierNotes() {
      const costRoles = this.operationBalanceCostRoleKeys;
      return this.operationBalanceAllSupplierNotes.filter(
        (note) => !costRoles.has(this.notaComercialRoleKey(note))
      );
    },
    hasPayments() {
      // Check if there are any payments (paid or pending) in the payment messages
      if (!this.paymentMessages || this.paymentMessages.length === 0) {
        return false;
      }
      return this.paymentMessages.some(message => {
        if (message.totals) {
          return (message.totals.paid > 0 || message.totals.requested > 0 || message.totals.pending > 0);
        }
        return false;
      });
    },
    // Verificar si el usuario tiene permiso para cobrar (collects)
    canCollect() {
      // Si tiene finance.manage, puede hacer todo
      if (this.hasPermission('finance.manage')) {
        return true;
      }
      // Si tiene finance.collects, puede cobrar
      return this.hasPermission('finance.collects');
    },
    // Verificar si el usuario tiene permiso para hacer pagos
    canMakePayments() {
      // Si tiene finance.manage, puede hacer todo
      if (this.hasPermission('finance.manage')) {
        return true;
      }
      // Si tiene finance.payments, puede hacer pagos
      return this.hasPermission('finance.payments');
    },
    /** Botón «Auto load expenses from negotiation»: proforma de fobtrading que viene de una negociación. */
    canLoadNegotiationExpenses() {
      return (
        this.fobtradingModuleActive &&
        this.canMakePayments &&
        !!(this.itemData && this.itemData._id && this.itemData.negotiation)
      );
    },
    /** Administrar finanzas (`finance.manage`): marcar pagos como pagados (paid), mismo criterio que el dashboard finance. */
    canFinanceManagePayments() {
      if (localStorage.getItem("role") === "admin") return true;
      return this.hasPermission("finance.manage");
    },
    /** Borrar un pago: el endpoint permanente pide `payment.manage` (o admin), no `finance.manage`. */
    canDeletePayments() {
      return canDeletePaymentsFor((perm) => this.hasPermission(perm));
    },
    /** Embarque con id: mostrar bloque Payments Summary aunque aún no haya montos ni gastos */
    showPaymentsSummaryShell() {
      return this.isShipping && !!(this.itemData && this.itemData._id);
    },
    /** Proforma: API devuelve collectionStagesVisual (etapas 1 + 2). */
    showProformCollectionStagesVisual() {
      return (
        this.isProform &&
        this.financialData &&
        !this.loading &&
        !!this.financialData.collectionStagesVisual
      );
    },
    /** Igual que showProformCollectionStagesVisual pero solo si el módulo FOB Trading está activo (integración CollectionSummary). */
    showProformCollectionStagesForUi() {
      return this.showProformCollectionStagesVisual && this.fobtradingModuleActive;
    },
    collectionStagesVisual() {
      if (!this.financialData || !this.financialData.collectionStagesVisual) return null;
      return this.financialData.collectionStagesVisual;
    },
    quickCollectStage1AutoShipSplitContext() {
      return buildStage1AutoShipSplitContext({
        extraParams: this.extraParams,
        parentModel: this.parentModel || this.modelName,
        collectionStagesVisual: this.collectionStagesVisual,
        amountMax: this.quickCollectAmountMax,
      });
    },
    resolvedCollectionClientId() {
      if (this.financialData?.targetClientId) {
        return String(this.financialData.targetClientId);
      }
      return this.extractClientId(this.itemData?.client);
    },
    resolvedCollectionClientName() {
      if (this.financialData?.targetClientName) {
        return this.financialData.targetClientName;
      }
      return this.extractClientName(this.itemData?.client);
    },
    notaComercialQuickAllowed() {
      if (!this.notaComercialModuleActive) return false;
      return !!(this.canCollect || this.canMakePayments || this.canFinanceManagePayments);
    },
    /** Proforma del shipping (`custom_proform`), ya sea objeto poblado o id suelto. */
    shippingProformId() {
      if (!this.isShipping) return "";
      const ref = this.itemData && this.itemData.custom_proform;
      if (!ref) return "";
      if (typeof ref === "object") return ref._id ? String(ref._id) : "";
      return String(ref);
    },
    /** Atajo a la operación FOB desde el panel de un shipping (no dentro de la propia operación). */
    showShippingOperacionAction() {
      if (!this.fobtradingModuleActive) return false;
      if (!this.shippingProformId) return false;
      return this.$route && this.$route.name !== "FobtradingOperacion";
    },
  },
  watch: {
    itemData: {
      immediate: true,
      deep: true,
      handler() {
        if (this.isProform || this.isShipping) {
          if (!this.balanceOnly) this.loadFinancialData();
          // Load balance for both proforms and shippings
          this.loadBalanceData();
          // Load payment data only for shippings
          if (this.isShipping) {
            this.loadPaymentData();
          }
        }
      },
    },
  },
  methods: {
    /**
     * Tipo de cambio de una moneda en la operación; la del sistema vale 1. En la PI, el del
     * embarque `shippingId` (con un solo embarque, el de ese).
     */
    fxEntryFor(currencyId, shippingId = null) {
      const fx = this.balanceFx;
      const id =
        currencyId && typeof currencyId === "object" && currencyId._id != null
          ? String(currencyId._id)
          : String(currencyId || "");
      const sid = this.fxShippingIdFor(shippingId);
      if (!fx || !id || id === String(fx.reportingCurrencyId)) {
        return { currencyId: id || (fx && fx.reportingCurrencyId), rate: 1, source: "system", shippingId: sid };
      }
      const found = this.fxRatesFor(sid).find((r) => r && String(r.currencyId) === id);
      return found
        ? { ...found, shippingId: sid }
        : { currencyId: id, rate: null, source: null, missing: true, shippingId: sid };
    },
    /** Tipos de cambio de la operación, o en la PI los del embarque. */
    fxRatesFor(shippingId) {
      const fx = this.balanceFx;
      if (!fx) return [];
      if (!fx.byShipping) return fx.rates || [];
      return (shippingId && fx.byShipping[String(shippingId)]) || [];
    },
    /** En la PI con un solo embarque, toda fila es de ese embarque. */
    fxShippingIdFor(shippingId) {
      if (shippingId) return String(shippingId);
      const fx = this.balanceFx;
      if (!fx || !fx.byShipping) return null;
      const ids = Object.keys(fx.byShipping);
      return ids.length === 1 ? ids[0] : null;
    },
    /**
     * Tipo de cambio de una fila hecha de partes (en la PI, una por embarque): el de todas
     * si coincide, «varios» si no. Si junta varios embarques no se edita desde la fila.
     */
    fxEntryForParts(parts) {
      const list = (parts || []).filter(Boolean);
      if (!list.length) return null;
      const entries = list.map((part) => this.fxEntryFor(part.currencyId, part.shippingId));
      const first = entries[0];
      const same = entries.every(
        (e) =>
          String(e.currencyId) === String(first.currencyId) &&
          e.rate === first.rate &&
          e.source === first.source
      );
      if (!same) return { currencyId: first.currencyId, rate: null, source: "mixed", mixed: true };
      const shippings = new Set(entries.map((e) => String(e.shippingId || "")));
      return shippings.size > 1 ? { ...first, shippingId: null, shared: true } : first;
    },
    /** Suma de las partes en la moneda del sistema; null si a alguna le falta cotización. */
    fxUsdForParts(parts) {
      let total = 0;
      for (const part of parts || []) {
        const rate = this.fxEntryFor(part.currencyId, part.shippingId).rate;
        if (!rate) return null;
        total += (Number(part.amount) || 0) * rate;
      }
      return total;
    },
    fxUsdPartsDisplay(parts) {
      const usd = this.fxUsdForParts(parts);
      return usd == null ? "—" : this.fxReportingPrefix + this.formatNumber(usd);
    },
    /**
     * Equivalente en la moneda del sistema de un importe que no está en ella, para mostrar
     * debajo y entre paréntesis sin sumar columnas (#397): «(USD 28,118.18)». Vacío si ya
     * está en USD, si los embarques tienen tipos de cambio distintos o si falta cotización.
     */
    fxUsdHint(entry, amount) {
      if (!this.balanceFx || !entry || entry.source === "system" || entry.mixed || !entry.rate) return "";
      const value = Number(amount);
      if (!Number.isFinite(value)) return "";
      return `(${this.fxReportingLabel} ${this.formatMarginAmount(value * entry.rate)})`;
    },
    /** Invoice de un embarque de la PI, en la moneda de la PI. */
    fxShippingInvoiceParts(ship) {
      const client = (this.balanceData && this.balanceData.client) || {};
      return [{ shippingId: ship._id, currencyId: ship.invoiceCurrencyId || client.currencyId, amount: ship.invoice }];
    },
    /** Un costo; en la PI, el mismo gasto en cada embarque (misma moneda). */
    fxCostParts(cost) {
      const bd = this.balanceData;
      if (!bd || !bd.isProform) return [{ shippingId: null, currencyId: cost.currencyId, amount: cost.amount }];
      const parts = [];
      for (const ship of bd.shippings || []) {
        for (const c of ship.costs || []) {
          if (c.name === cost.name && String(c.currencyId || "") === String(cost.currencyId || "")) {
            parts.push({ shippingId: ship._id, currencyId: c.currencyId, amount: c.amount });
          }
        }
      }
      return parts;
    },
    /** Factura del proveedor de un embarque, con el mismo criterio que la fila consolidada. */
    shipSupplierDue(ship) {
      if (this.operationBalanceSupplierNotes.length) return Number(ship.supplierInvoice) || 0;
      const eff = ship.supplierInvoiceEffective;
      if (eff != null && eff !== "" && Number.isFinite(Number(eff))) return Number(eff);
      return Number(ship.supplierInvoice) || 0;
    },
    /**
     * Importe de la fila del proveedor. En una PI cuyos embarques le pagan al proveedor en
     * monedas distintas, en la del sistema: la suma cruda mezclaría monedas.
     */
    supplierAmountDisplay(field) {
      const bd = this.balanceData;
      const s = (bd && bd.supplier) || {};
      const value = field === "due" ? this.balanceSupplierExpenseDueDisplay : Number(s[field]) || 0;
      if (bd && bd.isProform && s.currencyMixed && this.balanceFx) {
        const parts = (bd.shippings || []).map((ship) => ({
          shippingId: ship._id,
          currencyId: ship.supplierCurrencyId,
          amount:
            field === "due"
              ? this.shipSupplierDue(ship)
              : field === "paid"
                ? ship.supplierPaid
                : ship.supplierBalance,
        }));
        const usd = this.fxUsdForParts(parts);
        if (usd != null) return this.fxReportingPrefix + this.formatNumber(usd);
      }
      return this.supplierMoneyPrefix + this.formatNumber(value);
    },
    /** Importe en la moneda del sistema con el tipo de cambio de la operación. */
    fxUsdDisplay(amount, currencyId) {
      const rate = this.fxEntryFor(currencyId).rate;
      if (!rate) return "—";
      return this.fxReportingPrefix + this.formatNumber((Number(amount) || 0) * rate);
    },
    fxCurrencyLabel(currencyId) {
      const id = String(currencyId || "");
      const found = this.operationCurrencyRows.find((v) => String(v._id) === id);
      const fx = this.balanceFx;
      const allRates = fx ? (fx.byShipping ? Object.values(fx.byShipping).flat() : fx.rates || []) : [];
      const fromRates = allRates.find((r) => r && String(r.currencyId) === id);
      return String((found && (found.label || found.name)) || (fromRates && fromRates.label) || "")
        .trim()
        .toUpperCase();
    },
    openFxModal(currencyId, shippingId = null) {
      this.fxModalCurrencyId = currencyId ? String(currencyId) : null;
      this.fxModalShippingId = shippingId ? String(shippingId) : null;
    },
    closeFxModal() {
      this.fxModalCurrencyId = null;
      this.fxModalShippingId = null;
    },
    /** El backend ya recalculó los márgenes con el tipo de cambio nuevo. */
    async onFxSaved() {
      this.fxModalCurrencyId = null;
      this.fxModalShippingId = null;
      await this.loadBalanceData();
    },
    openShippingOperacionView() {
      if (!this.shippingProformId) return;
      this.$router.push({
        name: "FobtradingOperacion",
        params: { proformId: this.shippingProformId },
      });
    },
    onEntriesGenerated(results) {
      console.log('Accounting entries generated:', results);
      // Optionally refresh data after generation
      this.refreshData();
    },

    parseInlineAdvancePct() {
      const raw = this.inlineAdvancePctDraft;
      if (raw === "" || raw === null || raw === undefined) return NaN;
      const n = parseInt(String(raw).trim(), 10);
      return Number.isFinite(n) ? n : NaN;
    },

    async saveInlineProformAdvance() {
      if (!this.isProform || !this.itemData?._id || this.savingInlineAdvance) return;
      const n = this.parseInlineAdvancePct();
      if (!Number.isFinite(n) || n < 1 || n > 100) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.advancePctInvalidTitle"),
          text: this.$tn("financialSummary.stages.advancePctInvalid"),
        });
        return;
      }
      this.savingInlineAdvance = true;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/${this.modelName}/${this.itemData._id}`, {
          advance: n,
          partialUpdate: true,
        });
        this.inlineAdvancePctDraft = "";
        await this.loadFinancialData();
        this.$emit("advance-pct-saved");
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.advancePctSaved"),
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("saveInlineProformAdvance:", error);
        const msg =
          (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
          error.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.advancePctSaveError"),
        });
      } finally {
        this.savingInlineAdvance = false;
      }
    },

    async loadFinancialData() {
      if (!this.itemData?._id) return;

      try {
        this.loading = true;
        
        // Call the financial summary endpoint
        const response = await wencesApi.get(
          `/api/financial/collects/${this.itemData._id}/details`
        );

        if (response.data.success) {
          this.processFinancialData(response.data);
        }
      } catch (error) {
        console.error("Error loading financial data:", error);
        // Don't show error to user, just hide the component
        this.summaryMessages = [];
      } finally {
        this.loading = false;
      }
    },

    async loadPaymentData() {
      if (!this.itemData?._id || !this.isShipping) return;

      try {
        this.loadingPayments = true;
        this.paymentMessages = [];

        const response = await wencesApi.get(
          `/api/financial/payments/${this.itemData._id}/details`
        );

        if (response.data.success) {
          this.processPaymentData(response.data);
        } else {
          this.processPaymentData({
            financialSummary: {},
            shippingItem: {},
            expenses: [],
            paymentsCount: 0,
          });
        }
      } catch (error) {
        console.error("Error loading payment data:", error);
        this.paymentData = null;
        this.paymentMessages = [
          {
            type: "warning",
            icon: "fas fa-exclamation-triangle",
            color: "#dc3545",
            text: this.$t("financialSummary.payments.summaryLoadError"),
            details: this.$t("financialSummary.payments.summaryLoadErrorDetails"),
          },
        ];
      } finally {
        this.loadingPayments = false;
      }
    },

    async loadBalanceData() {
      if (!this.itemData?._id) return;

      try {
        this.loadingBalance = true;
        
        // Different endpoint for proform vs shipping
        const endpoint = this.isProform 
          ? `/api/financial/payments/proform/${this.itemData._id}/balance`
          : `/api/financial/payments/${this.itemData._id}/balance`;
        
        const response = await wencesApi.get(endpoint);

        if (response.data.success) {
          if (this.isProform) {
            // Proform balance data includes consolidated shippings
            this.balanceData = {
              proform: response.data.proform,
              shippingsCount: response.data.shippingsCount,
              shippings: response.data.shippings,
              client: response.data.client,
              supplier: response.data.supplier,
              costs: response.data.costs,
              costsSummary: response.data.costsSummary,
              margins: response.data.margins,
              /** Tipo de cambio de cada embarque por moneda (exchange_rates_module, #397). */
              fx: response.data.fx || null,
              isProform: true,
              currentShippingId: null, // No current shipping when viewing proform
              invoiceLineContainersAllocatedByIndex:
                response.data.invoiceLineContainersAllocatedByIndex || [],
            };
          } else {
            // Single shipping: always use this shipping's balance (not proforma consolidated)
            this.balanceData = {
              shipping: response.data.shipping,
              proform: response.data.proform,
              client: response.data.client,
              supplier: response.data.supplier,
              costs: response.data.costs,
              costsSummary: response.data.costsSummary,
              margins: response.data.margins,
              /** Tipo de cambio de la operación por moneda (exchange_rates_module, #397). */
              fx: response.data.fx || null,
              isProform: false,
              currentShippingId: this.itemData._id,
              invoiceLineContainersAllocatedByIndex:
                response.data.invoiceLineContainersAllocatedByIndex || [],
              clientCreditNotes: Array.isArray(
                response.data.client && response.data.client.clientCreditNotes
              )
                ? response.data.client.clientCreditNotes
                : [],
            };
          }
        }
      } catch (error) {
        console.error("Error loading balance data:", error);
        this.balanceData = null;
      } finally {
        this.loadingBalance = false;
      }
    },

    processPaymentData(data) {
      this.paymentMessages = [];
      
      const summary = data.financialSummary || {};
      const shippingItem = data.shippingItem || {};
      const expenses = data.expenses || [];

      this.paymentData = {
        totalProformship: summary.totalProformship || 0,
        baseAmount: summary.baseAmount || 0,
        advancePercentage: summary.advancePercentage || 0,
        expectedAdvance: summary.expectedAdvance || 0,
        appliedAdvance: summary.appliedAdvance || 0,  // All applied (including pending)
        paidAdvance: summary.paidAdvance || 0,        // Only paid=true
        pendingAdvance: summary.pendingAdvance || 0,
        totalExpenses: summary.totalExpenses || 0,
        appliedExpenses: summary.appliedExpenses || 0, // All applied (including pending)
        paidExpenses: summary.paidExpenses || 0,       // Only paid=true
        pendingExpenses: summary.pendingExpenses || 0,
        totalToPay: summary.totalToPay || 0,
        totalPaid: summary.totalPaid || 0,
        totalPending: summary.totalPending || 0,
        paymentConditionLabel: shippingItem.paymentConditionLabel || null,
        expenses: expenses,
        paymentsCount: data.paymentsCount || 0,
        prepaymentExpenseId: summary.prepaymentExpenseId || null,
        prepaymentExpenseContact: ncContactId(summary.prepaymentExpenseContact),
        prepaymentCurrencyId: summary.prepaymentCurrencyId || null,
        hasPendingAdvanceRequest: summary.hasPendingAdvanceRequest || false,
      };

      this.generatePaymentMessages();
    },

    generatePaymentMessages() {
      if (!this.paymentData) return;

      const { 
        expectedAdvance, 
        paidAdvance, 
        pendingAdvance,
        totalExpenses,
        paidExpenses,
        pendingExpenses,
        totalPaid,
        totalPending,
        advancePercentage,
        paymentsCount
      } = this.paymentData;

      // Factura del proveedor en su moneda (puede no ser la de la operación, #397).
      const supplierPrefix = this.currencyPrefixFor(this.paymentData.prepaymentCurrencyId);
      // Message 1: Supplier advance status
      const { baseAmount, hasPendingAdvanceRequest, appliedAdvance } = this.paymentData;
      
      // Totals for supplier (prepayment expense)
      // requested = applied - paid (what's been requested but not paid yet)
      const supplierTotals = {
        total: baseAmount,
        requested: Math.max(0, (appliedAdvance || 0) - paidAdvance),
        paid: paidAdvance,
        pending: Math.max(0, baseAmount - paidAdvance),
      };
      
      // Case 1: No advance configured (0%) - show full payment option
      if (expectedAdvance === 0 && baseAmount > 0) {
        if (paidAdvance === 0) {
          // Nothing paid yet - show button to pay 100%
          const message = {
            type: "info",
            icon: "fas fa-truck",
            color: "#17a2b8",
            text: this.$t("financialSummary.payments.supplierInvoicePending", {
              amount: supplierPrefix + this.formatNumber(baseAmount),
            }),
            details: hasPendingAdvanceRequest 
              ? this.$t("financialSummary.payments.requestAlreadySent")
              : this.$t("financialSummary.payments.supplierInvoicePendingDetails"),
            totals: supplierTotals,
            currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
          };
          message.action = {
            label: this.$t("financialSummary.payments.requestFull"),
            icon: "fas fa-dollar-sign",
            class: "btn-outline-danger",
            type: "addPayment",
            paymentType: "full",
            disabled: hasPendingAdvanceRequest,
            disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
          };
          this.paymentMessages.push(message);
        } else if (paidAdvance < baseAmount) {
          // Partial payment
          const percentage = Math.round((paidAdvance / baseAmount) * 100);
          const remaining = baseAmount - paidAdvance;
          const message = {
            type: "warning",
            icon: "fas fa-clock",
            color: "#fd7e14",
            text: this.$t("financialSummary.payments.supplierInvoicePartial", {
              paid: supplierPrefix + this.formatNumber(paidAdvance),
              total: supplierPrefix + this.formatNumber(baseAmount),
              remaining: supplierPrefix + this.formatNumber(remaining),
              percentage: percentage,
            }),
            details: hasPendingAdvanceRequest 
              ? this.$t("financialSummary.payments.requestAlreadySent")
              : null,
            totals: supplierTotals,
            currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
          };
          message.action = {
            label: this.$t("financialSummary.payments.requestRemaining"),
            icon: "fas fa-dollar-sign",
            class: "btn-outline-warning",
            type: "addPayment",
            paymentType: "full",
            disabled: hasPendingAdvanceRequest,
            disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
          };
          this.paymentMessages.push(message);
        } else {
          // Fully paid
          this.paymentMessages.push({
            type: "success",
            icon: "fas fa-check-circle",
            color: "#28a745",
            text: this.$t("financialSummary.payments.supplierInvoiceComplete", {
              amount: supplierPrefix + this.formatNumber(paidAdvance),
            }),
            totals: supplierTotals,
            currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
          });
        }
      }
      // Case 2: Advance configured - existing logic
      else if (expectedAdvance > 0) {
        if (paidAdvance === 0) {
          // Nothing paid yet
          const message = {
            type: "info",
            icon: "fas fa-truck",
            color: "#17a2b8",
            text: this.$t("financialSummary.payments.advancePending", {
              percentage: advancePercentage,
              amount: supplierPrefix + this.formatNumber(expectedAdvance),
            }),
            details: hasPendingAdvanceRequest 
              ? this.$t("financialSummary.payments.requestAlreadySent")
              : this.$t("financialSummary.payments.advancePendingDetails"),
            totals: supplierTotals,
            currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
          };
          message.actions = [
            {
              label: this.$t("financialSummary.payments.requestAdvance", { percentage: advancePercentage }),
              icon: "fas fa-percentage",
              class: "btn-outline-danger",
              type: "addPayment",
              paymentType: "advance",
              disabled: hasPendingAdvanceRequest,
              disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
            },
            {
              label: this.$t("financialSummary.payments.requestFull"),
              icon: "fas fa-dollar-sign",
              class: "btn-outline-primary",
              type: "addPayment",
              paymentType: "full",
              disabled: hasPendingAdvanceRequest,
              disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
            },
          ];
          this.paymentMessages.push(message);
        } else if (paidAdvance < expectedAdvance) {
          // Partial advance paid
          const percentage = Math.round((paidAdvance / expectedAdvance) * 100);
          const message = {
            type: "warning",
            icon: "fas fa-clock",
            color: "#fd7e14",
            text: this.$t("financialSummary.payments.advancePartial", {
              paid: supplierPrefix + this.formatNumber(paidAdvance),
              expected: supplierPrefix + this.formatNumber(expectedAdvance),
              remaining: supplierPrefix + this.formatNumber(pendingAdvance),
              percentage: percentage,
            }),
            details: hasPendingAdvanceRequest 
              ? this.$t("financialSummary.payments.requestAlreadySent")
              : this.$t("financialSummary.payments.advancePartialDetails"),
            totals: supplierTotals,
            currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
          };
          message.actions = [
            {
              label: this.$t("financialSummary.payments.requestRemaining"),
              icon: "fas fa-arrow-up",
              class: "btn-outline-warning",
              type: "addPayment",
              paymentType: "advance",
              disabled: hasPendingAdvanceRequest,
              disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
            },
            {
              label: this.$t("financialSummary.payments.requestFull"),
              icon: "fas fa-dollar-sign",
              class: "btn-outline-primary",
              type: "addPayment",
              paymentType: "full",
              disabled: hasPendingAdvanceRequest,
              disabledHint: hasPendingAdvanceRequest ? "requestAlreadySent" : null,
            },
          ];
          this.paymentMessages.push(message);
        } else {
          // Advance complete
          const { appliedAdvance } = this.paymentData;
          const balanceAmount = baseAmount - paidAdvance;
          const remainingToRequest = baseAmount - appliedAdvance; // What's left to request
          
          if (paidAdvance < baseAmount) {
            // Advance complete but not full amount - need to pay balance
            if (remainingToRequest <= 0 && appliedAdvance > paidAdvance) {
              // Balance already requested but not paid yet
              this.paymentMessages.push({
                type: "warning",
                icon: "fas fa-clock",
                color: "#fd7e14",
                text: this.$t("financialSummary.payments.advanceComplete", {
                  amount: supplierPrefix + this.formatNumber(paidAdvance),
                }),
                details: this.$t("financialSummary.payments.balanceRequested", {
                  amount: supplierPrefix + this.formatNumber(balanceAmount),
                }),
                totals: supplierTotals,
                currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
                action: {
                  label: this.$t("financialSummary.payments.requestBalance"),
                  icon: "fas fa-dollar-sign",
                  class: "btn-outline-primary",
                  type: "addPayment",
                  paymentType: "balance",
                  disabled: true,
                  disabledHint: "balanceAwaitingHint",
                },
              });
            } else {
              // Balance not yet requested - show button
              this.paymentMessages.push({
                type: "success",
                icon: "fas fa-check-circle",
                color: "#28a745",
                text: this.$t("financialSummary.payments.advanceComplete", {
                  amount: supplierPrefix + this.formatNumber(paidAdvance),
                }),
                details: this.$t("financialSummary.payments.balancePending", {
                  amount: supplierPrefix + this.formatNumber(balanceAmount),
                }),
                totals: supplierTotals,
                currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
                actions: [
                  {
                    label: this.$t("financialSummary.payments.requestBalance"),
                    icon: "fas fa-dollar-sign",
                    class: "btn-outline-primary",
                    type: "addPayment",
                    paymentType: "balance",
                    disabled: false,
                    disabledHint: null,
                  },
                ],
              });
            }
          } else {
            // Everything paid - no buttons needed
            this.paymentMessages.push({
              type: "success",
              icon: "fas fa-check-circle",
              color: "#28a745",
              text: this.$t("financialSummary.payments.advanceComplete", {
                amount: supplierPrefix + this.formatNumber(paidAdvance),
              }),
              details: this.$t("financialSummary.payments.advanceCompleteDetails"),
              totals: supplierTotals,
              currencyPrefix: supplierPrefix,
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: this.paymentData.prepaymentExpenseContact,
            });
          }
        }
      }

      // Case 3: la factura del proveedor todavía no tiene importe (precios en 0, sin
      // gasto de proveedor). No hay nada que pagarle, pero sus notas comerciales se
      // tienen que poder asignar igual: sin esta línea no quedaba ningún botón.
      else if (!(baseAmount > 0) && this.isShipping) {
        const supplierId =
          this.paymentData.prepaymentExpenseContact ||
          ncContactId(this.itemData && this.itemData.shipper);
        if (supplierId) {
          this.paymentMessages.push({
            type: "info",
            icon: "fas fa-truck",
            color: "#6c757d",
            text: this.$t("financialSummary.payments.supplierInvoiceNoAmount"),
            details: this.$t("financialSummary.payments.supplierInvoiceNoAmountDetails"),
            expenseId: this.paymentData.prepaymentExpenseId,
            expenseContactId: supplierId,
          });
        }
      }

      // Message 2: Individual expenses status
      const expenses = this.paymentData.expenses || [];
      
      for (const expense of expenses) {
        const remaining = expense.remaining || 0;        // What's left to request
        const amount = expense.amount || 0;
        const paidAmount = expense.paidAmount || 0;      // Actually paid (paid=true)
        const applied = expense.applied || 0;            // All applied (including pending)
        const hasPendingRequest = expense.hasPendingRequest || false;
        // Cada gasto con el signo de la moneda con que se cargó (#397).
        const expensePrefix = this.currencyPrefixFor(expense.currencyId);
        
        // Totals for this expense
        // requested = applied - paid (what's been requested but not paid yet)
        const expenseTotals = {
          total: amount,
          requested: Math.max(0, applied - paidAmount),
          paid: paidAmount,
          pending: Math.max(0, amount - paidAmount),
        };
        
        // Use paidAmount to determine display status (not applied)
        if (paidAmount === 0) {
          if (remaining > 0) {
            // Nothing paid yet, still has remaining to request
            const message = {
              type: "info",
              icon: "fas fa-receipt",
              color: "#6c757d",
              text: `📋 <strong>${expense.name || this.$t("financialSummary.payments.expense")}:</strong> ${expensePrefix}${this.formatNumber(amount)} ${this.$t("financialSummary.payments.toPay")}${this.expenseNegotiationFreightSuffix(expense)}`,
              details: hasPendingRequest 
                ? this.$t("financialSummary.payments.requestAlreadySent")
                : this.$t("financialSummary.payments.expenseNotPaidDetails"),
              totals: expenseTotals,
              currencyPrefix: expensePrefix,
              expenseId: expense._id,
              expenseContactId: ncContactId(expense.contact),
            };
            message.action = {
              label: this.$t("financialSummary.payments.payExpense"),
              icon: "fas fa-dollar-sign",
              class: "btn-outline-danger",
              type: "payExpense",
              expenseId: expense._id,
              expenseContactId: ncContactId(expense.contact),
              expenseName: expense.name,
              amountMax: remaining,
              disabled: hasPendingRequest,
              disabledHint: hasPendingRequest ? "requestAlreadySent" : null,
            };
            this.paymentMessages.push(message);
          } else if (applied > 0) {
            // All requested but nothing paid yet
            this.paymentMessages.push({
              type: "warning",
              icon: "fas fa-clock",
              color: "#fd7e14",
              text: `⏳ <strong>${expense.name || this.$t("financialSummary.payments.expense")}:</strong> ${expensePrefix}${this.formatNumber(amount)} ${this.$t("financialSummary.payments.requested")}${this.expenseNegotiationFreightSuffix(expense)}`,
              details: this.$t("financialSummary.payments.awaitingPayment"),
              totals: expenseTotals,
              currencyPrefix: expensePrefix,
              expenseId: expense._id,
              expenseContactId: ncContactId(expense.contact),
              action: {
                label: this.$t("financialSummary.payments.payExpense"),
                icon: "fas fa-dollar-sign",
                class: "btn-outline-warning",
                type: "payExpense",
                expenseId: expense._id,
                expenseContactId: ncContactId(expense.contact),
                expenseName: expense.name,
                disabled: true,
                disabledHint: "awaitingPayment",
              },
            });
          }
        } else if (paidAmount < amount) {
          // Expense partially paid
          const percentage = Math.round((paidAmount / amount) * 100);
          const realRemaining = amount - paidAmount;
          const message = {
            type: "warning",
            icon: "fas fa-receipt",
            color: "#fd7e14",
            text: `⏳ <strong>${expense.name || this.$t("financialSummary.payments.expense")}:</strong> ${expensePrefix}${this.formatNumber(paidAmount)} ${this.$t("financialSummary.payments.of")} ${expensePrefix}${this.formatNumber(amount)} (${percentage}%). <strong>${expensePrefix}${this.formatNumber(realRemaining)} ${this.$t("financialSummary.payments.remaining")}</strong>${this.expenseNegotiationFreightSuffix(expense)}`,
            details: hasPendingRequest 
              ? this.$t("financialSummary.payments.requestAlreadySent")
              : null,
            totals: expenseTotals,
            currencyPrefix: expensePrefix,
            expenseId: expense._id,
            expenseContactId: ncContactId(expense.contact),
          };
          message.action = {
            label: this.$t("financialSummary.payments.completeExpense"),
            icon: "fas fa-dollar-sign",
            class: "btn-outline-warning",
            type: "payExpense",
            expenseId: expense._id,
            expenseContactId: ncContactId(expense.contact),
            expenseName: expense.name,
            amountMax: realRemaining,
            disabled: hasPendingRequest || !(remaining > 0),
            disabledHint: hasPendingRequest
              ? "requestAlreadySent"
              : !(remaining > 0)
                ? "awaitingPayment"
                : null,
          };
          this.paymentMessages.push(message);
        } else if (amount > 0) {
          // Expense fully paid - no button needed
          this.paymentMessages.push({
            type: "success",
            icon: "fas fa-check-circle",
            color: "#28a745",
            text: `✅ <strong>${expense.name || this.$t("financialSummary.payments.expense")}:</strong> ${expensePrefix}${this.formatNumber(paidAmount)} ${this.$t("financialSummary.payments.paid").toLowerCase()}${this.expenseNegotiationFreightSuffix(expense)}`,
            totals: expenseTotals,
            currencyPrefix: expensePrefix,
            expenseId: expense._id,
            expenseContactId: ncContactId(expense.contact),
          });
        }
      }

      // Nada que mostrar (sin monto proveedor / sin adelanto y sin gastos con movimiento): explicar al usuario
      if (this.paymentMessages.length === 0) {
        const expenseList = this.paymentData.expenses || [];
        const hasExpenseRows = expenseList.some(
          (e) =>
            (e.amount || 0) > 0 ||
            (e.remaining || 0) > 0 ||
            (e.applied || 0) > 0 ||
            (e.paidAmount || 0) > 0
        );
        const noSupplier = (baseAmount || 0) <= 0 && (expectedAdvance || 0) <= 0;
        this.paymentMessages.push({
          type: "info",
          icon: "fas fa-receipt",
          color: "#6c757d",
          text: this.$t("financialSummary.payments.emptySummaryTitle"),
          details:
            noSupplier && !hasExpenseRows
              ? this.$t("financialSummary.payments.emptySummaryDetailsNoExpenses")
              : this.$t("financialSummary.payments.emptySummaryDetailsGeneric"),
        });
      }
    },

    processFinancialData(data) {
      this.summaryMessages = [];
      
      const targetItem = data.targetItem || {};
      const collections = data.collections || [];

      // Calculate financial metrics
      const totalProform = targetItem.total_proform || this.itemData.total_proform || 0;
      
      // Get advance percentage - priority: targetItem.advance > itemData.advance > extracting from paymentConditionLabel
      let advancePercentage = 0;
      if (targetItem.advance) {
        advancePercentage = targetItem.advance;
      } else if (this.itemData.advance) {
        advancePercentage = this.itemData.advance;
      } else if (targetItem.paymentConditionLabel) {
        // Try to extract percentage from label like "30% Advance" or "30%"
        const match = targetItem.paymentConditionLabel.match(/(\d+)\s*%/);
        if (match) {
          advancePercentage = parseInt(match[1]);
        }
      }
      
      // For shippings, use 100% (full invoice amount)
      if (this.isShipping) {
        advancePercentage = 100;
      }
      
      // Calculate expected advance
      // For shippings: use expectedAmount from backend (already net of applicable deductions)
      // For proforms: use the percentage calculation
      let expectedAdvance;
      let proformAdvanceDeducted = 0;
      let shippingCollectedApplied = 0;
      
      if (this.isShipping && targetItem.expectedAmount !== undefined) {
        // Use backend-calculated expectedAmount for shippings
        expectedAdvance = targetItem.expectedAmount;
        proformAdvanceDeducted =
          targetItem.proformAdvanceDeducted ||
          targetItem.advanceAmountToSubtract ||
          0;
        shippingCollectedApplied = targetItem.shippingCollectedApplied || 0;
      } else {
        expectedAdvance = totalProform * (advancePercentage / 100);
      }
      
      // Use collectedAmount from backend (cash collects + available balance).
      // Fallback to sum of collections if not provided
      const totalCollectedRaw = data.collectedAmount !== undefined
        ? Number(data.collectedAmount) || 0
        : collections.reduce((sum, col) => sum + (Number(col.amount) || 0), 0);

      // Neto NC − ND cliente (Dar+Cliente) aplicado a este shipping/proforma.
      const point1CreditNotesGiven = Number(targetItem.point1CreditNotesGiven) || 0;

      // Collected = cobros reales. En shipping: cash + adelanto proforma. NC/ND no entran.
      let totalCollected;
      if (data.collectedTotal !== undefined && data.collectedTotal !== null && Number.isFinite(Number(data.collectedTotal))) {
        totalCollected = Number(data.collectedTotal) || 0;
      } else if (this.isShipping) {
        totalCollected = totalCollectedRaw + (Number(proformAdvanceDeducted) || 0);
      } else {
        totalCollected = totalCollectedRaw;
      }
      
      // Pending = facturado − cobrado − NC + ND (point1CreditNotesGiven = NC − ND).
      const pendingRaw = this.isShipping
        ? totalProform - totalCollected - point1CreditNotesGiven
        : expectedAdvance - totalCollected;
      const totalPending = Math.max(0, pendingRaw);
      const totalOverCollected = pendingRaw < -0.01 ? Math.abs(pendingRaw) : 0;

      const tid =
        this.extractClientId(targetItem.client) || this.extractClientId(this.itemData.client);
      const tname = this.extractClientName(this.itemData.client);

      this.financialData = {
        totalProform,
        advancePercentage,
        expectedAdvance,
        totalCollected,
        totalPending,
        totalOverCollected,
        collectionsCount: collections.length,
        point1CreditNotesGiven,
        shippingsCount: targetItem.shippings?.length || this.itemData.shippings?.length || targetItem.shippingsCount || 0,
        paymentConditionLabel: targetItem.paymentConditionLabel || null,
        proformAdvanceDeducted,
        shippingCollectedApplied,
        aggregateShippingInvoicePending: Number(data.aggregateShippingInvoicePending) || 0,
        shippingsWithInvoicePendingCount: Number(data.shippingsWithInvoicePendingCount) || 0,
        linkedNotasComerciales: data.linkedNotasComerciales || {
          clientProform: [],
          clientByShipping: {},
          supplierByShipping: {},
        },
        collectionStagesVisual: this.isProform ? data.collectionStagesVisual || null : null,
        targetClientId: tid || null,
        targetClientName: tname || "",
      };

      // Generate contextual messages
      this.generateMessages();
      this.emitAdvancePendingByShipping();
    },

    /**
     * Ticket #328: anticipo pendiente de cada embarque, con el mismo desglose que
     * muestra esta pantalla. Cuenta como cobrado tanto lo asignado al adelanto como
     * lo cobrado solo al embarque, igual que `remaining_advance` de la proforma.
     */
    emitAdvancePendingByShipping() {
      if (!this.isProform) return;
      const stages =
        this.financialData && this.financialData.collectionStagesVisual;
      const rows =
        stages && Array.isArray(stages.stage2Shippings)
          ? stages.stage2Shippings
          : [];
      if (rows.length === 0) return;

      const pendingByShippingId = {};
      for (const row of rows) {
        if (!row || !row.shippingId) continue;
        const expected = Number(row.expectedAdvanceOnShipping) || 0;
        // Sin anticipo esperado por embarque no hay desglose: que decida el consumidor
        if (expected <= 0) continue;
        const collected =
          (Number(row.advanceAssignedAmount) || 0) +
          (Number(row.shippingOnlyCollectedAmount) || 0);
        pendingByShippingId[String(row.shippingId)] =
          Math.round(Math.max(0, expected - collected) * 100) / 100;
      }
      this.$emit("advance-pending-by-shipping", pendingByShippingId);
    },

    generateMessages() {
      if (!this.financialData) return;

      const { 
        totalProform, 
        advancePercentage, 
        expectedAdvance, 
        totalCollected, 
        totalPending,
        totalOverCollected,
        collectionsCount,
        shippingsCount,
        paymentConditionLabel
      } = this.financialData;

      // Different messages for Proform vs Shipping
      if (this.isProform) {
        if (this.financialData.collectionStagesVisual) {
          return;
        }
        // Message 1: Payment condition status (only for proforms)
        if (advancePercentage === 0) {
          this.summaryMessages.push({
            type: "warning",
            icon: "fas fa-exclamation-triangle",
            color: "#ffc107",
            text: this.$t("financialSummary.messages.noPaymentCondition"),
            details: this.$t("financialSummary.messages.noPaymentConditionDetails"),
            action: {
              label: this.$t("financialSummary.actions.configure"),
              icon: "fas fa-cog",
              class: "btn-outline-warning",
              type: "edit",
            },
          });
        } else {
          // Message 2: Advance payment status
          if (totalCollected === 0) {
            this.summaryMessages.push({
              type: "info",
              icon: "fas fa-info-circle",
              color: "#17a2b8",
              text: this.$t("financialSummary.messages.advancePending", {
                percentage: advancePercentage,
                amount: this.moneyPrefix + this.formatNumber(expectedAdvance),
              }),
              details: this.$t("financialSummary.messages.advancePendingDetails"),
              action: {
                label: this.$t("financialSummary.actions.addCollection"),
                icon: "fas fa-plus",
                class: "btn-outline-success",
                type: "addCollect",
              },
            });
          } else if (totalCollected < expectedAdvance) {
            const remaining = expectedAdvance - totalCollected;
            const percentage = Math.round((totalCollected / expectedAdvance) * 100);
            this.summaryMessages.push({
              type: "warning",
              icon: "fas fa-clock",
              color: "#fd7e14",
              text: this.$t("financialSummary.messages.advancePartial", {
                collected: this.moneyPrefix + this.formatNumber(totalCollected),
                expected: this.moneyPrefix + this.formatNumber(expectedAdvance),
                remaining: this.moneyPrefix + this.formatNumber(remaining),
                percentage: percentage,
              }),
              details: this.$t("financialSummary.messages.advancePartialDetails", {
                count: collectionsCount,
              }),
              action: {
                label: this.$t("financialSummary.actions.receivePayment"),
                icon: "fas fa-arrow-down",
                class: "btn-outline-success",
                type: "addCollect",
              },
            });
          } else if (totalOverCollected > 0.01) {
            this.summaryMessages.push({
              type: "warning",
              icon: "fas fa-exclamation-triangle",
              color: "#fd7e14",
              text: this.$t("financialSummary.overCollectedWarning", {
                amount: this.moneyPrefix + this.formatNumber(totalOverCollected),
              }),
              details: this.$t("financialSummary.stages.overCollectedAdvanceHint", {
                amount: this.moneyPrefix + this.formatNumber(totalOverCollected),
              }),
            });
          } else {
            this.summaryMessages.push({
              type: "success",
              icon: "fas fa-check-circle",
              color: "#28a745",
              text: this.$t("financialSummary.messages.advanceComplete", {
                amount: this.formatNumber(totalCollected),
                percentage: advancePercentage,
                expected: this.formatNumber(expectedAdvance),
              }),
              details: this.$t("financialSummary.messages.advanceCompleteDetails", {
                count: collectionsCount,
              }),
            });
          }
        }

        // Message 3: Shippings status (only for proforms)
        if (shippingsCount > 0) {
          this.summaryMessages.push({
            type: "info",
            icon: "fas fa-ship",
            color: "#6c757d",
            text: this.$t("financialSummary.messages.hasShippings", {
              count: shippingsCount,
            }),
            details: this.$t("financialSummary.messages.hasShippingsDetails", {
              advancePerShipping:
                this.moneyPrefix + this.formatNumber(expectedAdvance / shippingsCount),
            }),
          });
        } else if (advancePercentage > 0 && totalCollected >= expectedAdvance) {
          this.summaryMessages.push({
            type: "info",
            icon: "fas fa-ship",
            color: "#17a2b8",
            text: this.$t("financialSummary.messages.readyForShipping"),
            details: this.$t("financialSummary.messages.readyForShippingDetails"),
          });
        }

        const aggShipInv = Number(this.financialData.aggregateShippingInvoicePending) || 0;
        const cntShipInv = Number(this.financialData.shippingsWithInvoicePendingCount) || 0;
        if (aggShipInv > 0.01 && cntShipInv > 0) {
          this.summaryMessages.push({
            type: "info",
            icon: "fas fa-file-invoice-dollar",
            color: "#495057",
            text: this.$t("financialSummary.messages.shippingInvoicesPendingAfterAdvance", {
              total: this.formatNumber(aggShipInv),
              count: cntShipInv,
            }),
          });
        }
      } else if (this.isShipping) {
        // Messages for Shipping
        const totalInvoice = totalProform;
        const remainingToCollect = totalPending;
        const p1 = Number(this.financialData.point1CreditNotesGiven) || 0;
        const notesDetails =
          Math.abs(p1) > 0.01
            ? this.$t("financialSummary.stages.notaImpactExpectedNet", {
                amount:
                  p1 >= 0
                    ? "-" + this.moneyPrefix + this.formatNumber(p1)
                    : "+" + this.moneyPrefix + this.formatNumber(Math.abs(p1)),
              })
            : "";
        
        if (totalCollected === 0 && remainingToCollect > 0.01 && Math.abs(p1) <= 0.01) {
          this.summaryMessages.push({
            type: "info",
            icon: "fas fa-dollar-sign",
            color: "#17a2b8",
            text: `💰 <strong>${this.$t("financialSummary.shippingPending")}:</strong> ${this.moneyPrefix}${this.formatNumber(totalInvoice)} ${this.$t("financialSummary.toCollect")}`,
            details: this.$t("financialSummary.shippingPendingDetails"),
            action: {
              label: this.$t("financialSummary.actions.addCollection"),
              icon: "fas fa-plus",
              class: "btn-outline-success",
              type: "addCollect",
            },
          });
        } else if (remainingToCollect > 0.01) {
          const settled = Math.max(0, totalInvoice - remainingToCollect);
          const percentage = totalInvoice > 0 ? Math.round((settled / totalInvoice) * 100) : 0;
          let details;
          if (collectionsCount > 0) {
            details = `${collectionsCount} ${this.$t("financialSummary.collectionsRegistered")}`;
            if (notesDetails) details = `${details}. ${notesDetails}`;
          } else if (notesDetails) {
            details = notesDetails;
          } else {
            details = `${collectionsCount} ${this.$t("financialSummary.collectionsRegistered")}`;
          }
          this.summaryMessages.push({
            type: "warning",
            icon: "fas fa-clock",
            color: "#fd7e14",
            text: `⏳ <strong>${this.$t("financialSummary.shippingPartial")}:</strong> ${this.moneyPrefix}${this.formatNumber(totalCollected)} ${this.$t("financialSummary.of")} ${this.moneyPrefix}${this.formatNumber(totalInvoice)} (${percentage}%). <strong>${this.moneyPrefix}${this.formatNumber(remainingToCollect)} ${this.$t("financialSummary.remaining")}</strong>`,
            details,
            action: {
              label: this.$t("financialSummary.actions.receivePayment"),
              icon: "fas fa-arrow-down",
              class: "btn-outline-success",
              type: "addCollect",
            },
          });
        } else if (totalOverCollected > 0.01) {
          this.summaryMessages.push({
            type: "warning",
            icon: "fas fa-exclamation-triangle",
            color: "#fd7e14",
            text: this.$t("financialSummary.overCollectedWarning", {
              amount: this.moneyPrefix + this.formatNumber(totalOverCollected),
            }),
            details: this.$t("financialSummary.stages.overCollectedInvoiceHint", {
              amount: this.moneyPrefix + this.formatNumber(totalOverCollected),
            }),
          });
        } else {
          this.summaryMessages.push({
            type: "success",
            icon: "fas fa-check-circle",
            color: "#28a745",
            text: `✅ <strong>${this.$t("financialSummary.shippingComplete")}:</strong> ${this.moneyPrefix}${this.formatNumber(totalCollected)} ${this.$t("financialSummary.collected")}`,
            details: notesDetails
              ? `${collectionsCount} ${this.$t("financialSummary.collectionsRegistered")}. ${notesDetails}`
              : `${collectionsCount} ${this.$t("financialSummary.collectionsRegistered")}`,
          });
        }
      }
    },

    /** Comparación proforma negotiation.freight (solo fobtrading + freight; viene en expense desde el API). */
    expenseNegotiationFreightSuffix(expense) {
      if (
        expense == null ||
        expense.negotiationFreight == null ||
        expense.negotiationFreight === ""
      ) {
        return "";
      }
      const amt = this.moneyPrefix + this.formatNumber(expense.negotiationFreight);
      return ` <small class="text-danger">(${this.$t(
        "financialSummary.payments.proformaNegotiationFreight",
        { amount: amt }
      )})</small>`;
    },

    formatNumber(number) {
      // Siempre mostrar decimales para valores monetarios en financial summary
      // Incluir 0 para que también se formatee correctamente
      return formatNumberSync(number || 0, null, true);
    },

    formatMarginAmount(number) {
      return formatNumberSync(number || 0, null, true, 2);
    },

    formatMarginPercent(margin, total) {
      const m = Number(margin) || 0;
      const t = Number(total) || 0;
      if (t <= 0) return "0.00";
      return ((m / t) * 100).toFixed(2);
    },

    /** Importe legible en el modal de confirmación (evita locale ambiguo en el popup). */
    quickCollectConfirmMoney(value) {
      const n = Number(value);
      const x = Number.isFinite(n) ? n : 0;
      return "$" + x.toFixed(2);
    },

    isEffectivelyZero(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return false;
      return Math.abs(num) < 0.00005;
    },

    clientBalancePendingDisplay(balance, explicitOverCollected) {
      const fromApi = Number(explicitOverCollected);
      if (Number.isFinite(fromApi) && fromApi > 0.01) {
        return { pending: 0, overCollected: fromApi };
      }
      const b = Number(balance);
      if (!Number.isFinite(b)) return { pending: 0, overCollected: 0 };
      if (b < -0.01) return { pending: 0, overCollected: Math.abs(b) };
      return { pending: Math.max(0, b), overCollected: 0 };
    },

    operationBalanceClientCreditNotes(shipping) {
      const notes = shipping && shipping.clientCreditNotes;
      return Array.isArray(notes) ? notes : [];
    },

    operationBalanceCollectionsSubrowAmount(shipping) {
      if (!shipping) return 0;
      const direct = Number(shipping.collectedDirect);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const collected = Number(shipping.collected) || 0;
      const notes = this.operationBalanceClientCreditNotes(shipping);
      const notesSum = notes.reduce((sum, note) => {
        const amt = Number(note && note.appliedTotal) || 0;
        return sum + (this.operationBalanceNotaIsDebit(note) ? -amt : amt);
      }, 0);
      return Math.max(0, collected - notesSum);
    },

    /**
     * True si la nota aumenta lo pendiente del embarque (resta del cobrado).
     * Usa `appliedKind` del backend porque en las notas recibidas del cliente el efecto
     * va al revés de `credit_debit`; el fallback cubre payloads viejos.
     */
    /**
     * Solo llevan PDF las notas que emitimos nosotros. Las `Receiving` las emite el
     * contacto y nos llegan como documento suyo: de esas no generamos nada.
     */
    notaComercialHasPdf(note) {
      const fd = String((note && note.flow_direction) || "").trim().toLowerCase();
      return fd === "giving";
    },

    operationBalanceNotaIsDebit(note) {
      const kind = note && note.appliedKind;
      if (kind === "credit" || kind === "debit") return kind === "debit";
      return String(note && note.credit_debit ? note.credit_debit : "").trim() === "Debit note";
    },

    operationBalanceNotaCollectedDisplay(note) {
      const amt = Number(note && note.appliedTotal) || 0;
      if (this.operationBalanceNotaIsDebit(note)) {
        return `-${this.moneyPrefix}${this.formatNumber(amt)}`;
      }
      return `${this.moneyPrefix}${this.formatNumber(amt)}`;
    },

    /**
     * True si la nota del proveedor aumenta lo que le debemos. En las notas de
     * proveedor la etiqueta coincide con el efecto: su nota de credito nos reduce
     * la factura y su nota de debito nos la aumenta.
     */
    /**
     * Notas comerciales ligadas a una fila del resumen de pagos.
     *
     * La fila es un gasto, y el balance ya trae las notas reparidas por gasto: se busca
     * ahí por id en vez de recalcular. Sin esto el resumen era el único lugar donde una
     * nota ligada no se veía, y había que bajar hasta el operation balance para saberlo.
     *
     * @param {object} message fila de paymentMessages
     * @returns {object[]}
     */
    paymentMessageNotes(message) {
      const expenseId = String((message && message.expenseId) || "").trim();
      if (!expenseId) return [];
      const costs = (this.balanceData && this.balanceData.costs) || [];
      const cost = costs.find((c) => c && String(c._id || "") === expenseId);
      if (cost) return this.operationBalanceNotesForCost(cost);
      // La factura del proveedor no está en `costs`: lleva las notas de su propio rol.
      const supplierExpenseId = String(
        (this.paymentData && this.paymentData.prepaymentExpenseId) || ""
      ).trim();
      return supplierExpenseId && supplierExpenseId === expenseId
        ? this.operationBalanceSupplierNotes
        : [];
    },

    /** Efecto neto de esas notas sobre lo que queda por pagar (negativo si lo bajan). */
    paymentMessageNotaNetEffect(message) {
      return this.paymentMessageNotes(message).reduce(
        (sum, note) =>
          sum +
          (this.operationBalanceSupplierNotaIsDebit(note)
            ? Number(note.appliedTotal) || 0
            : -(Number(note.appliedTotal) || 0)),
        0
      );
    },

    /** Lo que queda por pagar de esa fila una vez descontadas sus notas (nunca negativo). */
    paymentMessagePendingAfterNotes(message) {
      const pending = Number(message && message.totals && message.totals.pending) || 0;
      return Math.max(0, pending + this.paymentMessageNotaNetEffect(message));
    },

    /**
     * Texto del efecto, con el signo delante: «−$200.0000».
     * El símbolo lo pone quien llama: la operación puede no estar en dólares.
     */
    paymentMessageNotaEffectLabel(message, currencyPrefix = "$") {
      const value = this.paymentMessageNotaNetEffect(message);
      const sign = value < 0 ? "−" : "+";
      // La línea lleva el signo de la moneda de su gasto (#397); si no, el que se pasa.
      const prefix = message && message.currencyPrefix ? message.currencyPrefix : currencyPrefix;
      return `${sign}${prefix}${this.formatNumber(Math.abs(value))}`;
    },

    /**
     * Signo de moneda de una línea del resumen de pagos: la de su gasto (#397). El courier
     * en USD de una operación en EUR se mostraba con €.
     */
    paymentMessagePrefix(message) {
      return message && message.currencyPrefix ? message.currencyPrefix : this.moneyPrefix;
    },

    /** Texto comparable del rol: lo escriben el alta de la nota y la lista de tipos de gasto. */
    notaComercialRoleKey(note) {
      return String((note && note.contact_role) || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },
    /**
     * Notas que le corresponden a una fila de costo.
     * El backend manda en cada costo el rol con el que se le ligan las notas.
     */
    operationBalanceNotesForCost(cost) {
      const roleKey = String((cost && cost.notaRoleKey) || "").trim();
      if (!roleKey) return [];
      return this.operationBalanceAllSupplierNotes.filter(
        (note) => this.notaComercialRoleKey(note) === roleKey
      );
    },
    operationBalanceSupplierNotaIsDebit(note) {
      return String(note && note.credit_debit ? note.credit_debit : "").trim() === "Debit note";
    },
    /**
     * Importe de la nota en la columna Paid/Collect: la NC del proveedor cubre parte de
     * la factura (positiva) y su ND nos suma deuda (negativa), igual que en el bloque CLIENT.
     */
    operationBalanceSupplierNotaDisplay(note) {
      const amt = Math.abs(Number(note && note.appliedTotal) || 0);
      if (this.operationBalanceSupplierNotaIsDebit(note)) return `-${this.moneyPrefix}${this.formatNumber(amt)}`;
      return `${this.moneyPrefix}${this.formatNumber(amt)}`;
    },
    operationBalanceNotaComercialHref(notaId) {
      if (notaId == null || notaId === "") return "#";
      return `/dynamic-model/notacomercial/view/${encodeURIComponent(String(notaId))}`;
    },

    openNotaComercialPdf(notaId) {
      if (!notaId || !this.notaComercialModuleActive) return;
      const route = this.$router.resolve({
        name: "DocumentTemplate",
        params: { type: "nota-comercial", id: String(notaId) },
      });
      window.open(route.href, "_blank");
    },

    isCurrentShipping(shippingId) {
      if (!this.balanceData || !this.balanceData.currentShippingId) {
        return false;
      }
      return shippingId && shippingId.toString() === this.balanceData.currentShippingId.toString();
    },

    refreshData() {
      this.loadFinancialData();
      this.loadBalanceData();
      if (this.isShipping) {
        this.loadPaymentData();
      }
      // For proforms, balance data already includes payment information
      // so we don't need a separate call
      this.$emit("financial-data-changed");
    },

    /** Desde `CollectionSummary` tras cobro / embarque rápido: misma recarga que el resto del resumen. */
    refreshCollectionStagesAfterChange() {
      this.refreshData();
    },

    async loadNegotiationExpenses(ship) {
      if (this.loadingNegotiationExpenses) return;
      this.loadingNegotiationExpenses = true;
      let loaded = false;
      try {
        loaded = await loadNegotiationExpensesForShipping(String(this.itemData._id), ship);
      } finally {
        this.loadingNegotiationExpenses = false;
      }
      if (loaded) this.refreshData();
    },

    onCollectionSummaryEditItem(payload) {
      this.$emit("edit-item", payload);
    },

    isPaymentLikeAction(action) {
      return !!(action && (action.type === "addPayment" || action.type === "payExpense"));
    },
    paymentActionDisabled(action) {
      if (!action || !this.isPaymentLikeAction(action)) return false;
      if (action.disabled) return true;
      if (!this.canMakePayments) return true;
      return false;
    },
    paymentActionDisabledHint(action) {
      if (!action || !this.isPaymentLikeAction(action) || !this.paymentActionDisabled(action)) {
        return "";
      }
      if (!this.canMakePayments) {
        return this.$t("financialSummary.payments.actionDisabledNoPermission");
      }
      if (action.disabled && action.disabledHint) {
        return this.$t(`financialSummary.payments.${action.disabledHint}`);
      }
      return "";
    },
    paymentSectionActionDisabled(action) {
      if (!action) return false;
      if (action.type === "addCollect") return !this.canCollect;
      if (this.isPaymentLikeAction(action)) return this.paymentActionDisabled(action);
      return false;
    },
    paymentSectionActionDisabledHint(action) {
      if (!action) return "";
      if (action.type === "addCollect" && !this.canCollect) {
        return this.$t("financialSummary.payments.actionDisabledNoCollectPermission");
      }
      return this.paymentActionDisabledHint(action);
    },
    onPaymentSectionActionClick(action) {
      if (this.paymentSectionActionDisabled(action)) return;
      this.executeAction(action);
    },

    executeAction(action) {
      switch (action.type) {
        case "edit":
          this.$router.push(`/dynamic-model/${this.modelName}/edit/${this.itemData._id}`);
          break;
        case "addCollect":
          // Open modal to create collect with parent context
          this.openAddCollectionModal();
          break;
        case "addPayment":
          // Open modal to create payment with parent context
          this.openAddPaymentModal(action.paymentType || "advance");
          break;
        case "payExpense":
          // Solicitud rápida: solo monto; appliedto viene de defaultvalues (gasto).
          this.openPayExpenseModal(
            action.expenseId,
            action.expenseName,
            action.amountMax,
            action.amountSuggested,
            { splitSupplierMode: false }
          );
          break;
        case "divideSupplierPayment":
          this.openPayExpenseModal(
            action.expenseId,
            action.expenseName,
            action.amountMax,
            action.amountSuggested,
            {
              splitSupplierMode: true,
              shippingId: action.shippingId,
            }
          );
          break;
        case "editShippingExpense":
          this.openEditShippingExpenseModal(action.expenseId);
          break;
        case "markPaymentPaid":
          this.openMarkPaymentPaidModal(action.payment);
          break;
        case "quickMarkPaymentPaid":
          this.openQuickMarkPaymentPaidModal(action.payment);
          break;
        case "quickEditPayment":
          this.openQuickEditPaymentModal(action.payment);
          break;
        case "deletePayment":
          this.deleteLinkedPaymentFromPill(action.payment);
          break;
        default:
          console.log("Unknown action:", action.type);
      }
    },

    async openMarkPaymentPaidModal(payment) {
      if (!payment || !payment._id || !this.canFinanceManagePayments) return;
      await this.$nextTick();
      const ref = this.$refs.paymentMarkPaidModal;
      if (ref && typeof ref.open === "function") {
        await ref.open(payment);
      }
    },

    async openQuickMarkPaymentPaidModal(payment) {
      if (!payment || !payment._id || !this.canFinanceManagePayments) return;
      await this.$nextTick();
      const ref = this.$refs.quickMarkPaymentPaidModal;
      if (ref && typeof ref.open === "function") {
        await ref.open(payment);
      }
    },
    async openQuickEditPaymentModal(payment) {
      if (!payment || !payment._id || !this.canFinanceManagePayments) return;
      await this.$nextTick();
      const ref = this.$refs.quickMarkPaymentPaidModal;
      if (ref && typeof ref.open === "function") {
        await ref.open(payment, { editMode: true });
      }
    },

    /**
     * Borrado del pago desde la pastilla del gasto, sin pasar por el modal de edición.
     * Un pago ya marcado como pagado se puede borrar igual, pero el aviso lo dice antes.
     */
    async deleteLinkedPaymentFromPill(payment) {
      if (!payment || !payment._id || !this.canDeletePayments) return;
      const { deleted } = await confirmAndDeletePayment((k) => this.$t(k), payment);
      if (deleted) this.refreshData();
    },

    onPaymentMarkPaidModalSaved() {
      this.refreshData();
    },

    /**
     * Monto aún por cobrar en el contexto Finance actual (shipping o proforma).
     * @returns {number}
     */
    resolveNotaComercialCollectiblePending() {
      const fd = this.financialData;
      if (!fd) return 0;
      const pending = Number(fd.totalPending);
      if (Number.isFinite(pending)) return Math.max(0, pending);
      const total = Number(fd.totalProform) || 0;
      const collected = Number(fd.totalCollected) || 0;
      return Math.max(0, total - collected);
    },

    async ensureNotaComercialPendingAllowsCreate() {
      const pending = this.resolveNotaComercialCollectiblePending();
      if (pending > 0.01) return true;
      await Swal.fire({
        icon: "info",
        title: this.$tn("financialSummary.actions.notaComercialNoPendingTitle"),
        text: this.$tn("financialSummary.actions.notaComercialNoPendingText"),
      });
      return false;
    },

    async openNotaComercialNoteModal() {
      // Abrir siempre: el bloqueo de crédito en shipping ya cobrado es al guardar (backend + alert).
      // El débito sí se permite aunque no quede pendiente por cobrar.
      const modal = this.$refs.notaComercialNoteModal;
      if (!modal || typeof modal.open !== "function") return;
      modal.open({
        sourceModel: this.modelName,
        modelName: this.modelName,
        itemData: this.itemData,
        clientId: this.resolvedCollectionClientId,
        lockContact: !!this.resolvedCollectionClientId,
        namePrefix: "NC-FIN",
      });
    },

    /** Quita de esta operación una nota listada en el operation balance. */
    async unapplyNotaFromBalanceRow(note, targetType, targetId) {
      if (!note || !note._id) return;
      const ok = await this.unapplyNotaComercialFromOperation({
        noteId: note._id,
        noteName: note.name || note.number || "",
        targetType,
        targetId,
        operationName: (this.itemData && this.itemData.name) || "",
      });
      if (ok) this.refreshData();
    },

    /**
     * Nota comercial del proveedor de un gasto, desde la fila de Payments summary.
     *
     * Usa el mismo contexto que al crearla desde la línea de gastos: variante, contacto,
     * rol (el tipo del gasto), moneda y aplicación al embarque ya vienen cargados.
     */
    async openSupplierNoteForExpense(expenseId) {
      const id = expenseId != null ? String(expenseId).trim() : "";
      const modal = this.$refs.notaComercialNoteModal;
      if (!id || !modal || typeof modal.open !== "function") return;
      try {
        const { data: row } = await wencesApi.get(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(id)}`
        );
        if (!row || !row._id) return;
        const shippingId = String(row.shipping || row.parent || "");
        const shippingName =
          String((this.itemData && this.itemData._id) || "") === shippingId
            ? String((this.itemData && this.itemData.name) || "")
            : "";
        modal.open(
          notaComercialModalContextFromExpenseLine(row, this.lists, { shippingName })
        );
      } catch (e) {
        console.error("FinancialSummary: nota comercial desde el gasto", e);
        await Swal.fire({
          icon: "error",
          title: this.$tn("financialSummary.fobNotaComercial.errorTitle"),
          text: this.$tn("financialSummary.fobNotaComercial.addSupplierNoteFailed"),
        });
      }
    },

    handleNotaComercialNoteSaved() {
      this.refreshData();
    },

    async openAddCollectionModal() {
      try {
        this.showCollectContainerCalculator = false;
        this.quickCollectAllowAmountEdit = false;
        this.quickCollectAmountMax = null;
        if (this.isProform) {
          const s1 =
            this.collectionStagesVisual && this.collectionStagesVisual.stage1;
          let amountCap = null;
          if (s1) {
            const rem = Number(s1.remainingAmount);
            const exp = Number(s1.expectedAmount);
            if (Number.isFinite(rem) && rem > 0.01) {
              amountCap = rem;
            } else if (Number.isFinite(exp) && exp > 0.01) {
              amountCap = exp;
            }
          }
          this.quickCollectAllowAmountEdit = true;
          this.quickCollectAmountMax = amountCap;
        } else if (this.isShipping && this.financialData) {
          const pending = Number(this.financialData.totalPending);
          this.quickCollectAllowAmountEdit = true;
          this.quickCollectAmountMax =
            Number.isFinite(pending) && pending > 0.01 ? pending : null;
        }

        // Set parent context
        this.parentID = this.itemData._id;
        this.parentModel = this.isProform ? "Proform" : "shipping";
        this.extraParams = { type: "advance" };
        
        // Load lists for dropdowns
        await this.loadModalData();
        
        // Show modal
        this.showAddCollectionModal = true;
      } catch (error) {
        console.error("Error opening add collection modal:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToOpenCollectionModal"),
        });
      }
    },

    async loadModalData() {
      try {
        const response = await wencesApi.get("/api/gotrading/lists");
        if (response.data) {
          this.lists = response.data;
        }
      } catch (error) {
        console.error("Error loading lists:", error);
        // Don't show error, modal can still work with empty data
      }
    },

    closeAddCollectionModal() {
      this.showAddCollectionModal = false;
      this.parentID = null;
      this.parentModel = null;
      this.extraParams = {};
      this.quickCollectAllowAmountEdit = false;
      this.quickCollectAmountMax = null;
      this.showCollectContainerCalculator = false;
    },

    // View collection details
    async viewCollectionDetails() {
      try {
        this.showCollectionDetailsModal = true;
        this.loadingCollectionDetails = true;
        this.collectionDetails = null;

        const targetId = this.itemData._id;
        if (!targetId) {
          throw new Error("No valid ID found for collection details");
        }

        const response = await wencesApi.get(`/api/financial/collects/${targetId}/details`);

        if (response.data.success) {
          this.collectionDetails = response.data;
        } else {
          throw new Error(response.data.message || "Failed to load collection details");
        }
      } catch (error) {
        console.error("Error loading collection details:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.errorLoadingCollectionDetails"),
        });
        this.showCollectionDetailsModal = false;
      } finally {
        this.loadingCollectionDetails = false;
      }
    },

    closeCollectionDetailsModal() {
      this.showCollectionDetailsModal = false;
      this.collectionDetails = null;
    },

    // View payment details
    async viewPaymentDetails() {
      try {
        this.showPaymentDetailsModal = true;
        this.loadingPaymentDetails = true;
        this.paymentDetails = null;

        const targetId = this.itemData._id;
        if (!targetId) {
          throw new Error("No valid ID found for payment details");
        }

        // Get all payments (both paid and pending) for this shipping
        const response = await wencesApi.get(`/api/financial/payments/${targetId}/paid`);

        if (response.data.success) {
          // Calculate total to pay from paymentData if available
          const totalToPay = this.paymentData?.totalToPay || 0;
          
          // The endpoint now returns all payments (paid and pending) in the payments array
          this.paymentDetails = {
            payments: response.data.payments || [],
            totalPaid: response.data.totalPaid || 0,
            totalToPay: totalToPay
          };
        } else {
          throw new Error(response.data.message || "Failed to load payment details");
        }
      } catch (error) {
        console.error("Error loading payment details:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.errorLoadingPaymentDetails"),
        });
        this.showPaymentDetailsModal = false;
      } finally {
        this.loadingPaymentDetails = false;
      }
    },

    closePaymentDetailsModal() {
      this.showPaymentDetailsModal = false;
      this.paymentDetails = null;
    },

    /**
     * Gastos detrás de las filas del cuadro SUPPLIER (Expenses), normalizados a una sola forma.
     * En la vista de proforma el cuadro consolida por tipo —un «Other» de $4.626.000 que en
     * realidad son dos gastos de proveedores distintos—, así que de la fila sola no se deduce
     * de dónde sale el número ni con qué pagos se cubre.
     */
    expenseBreakdownRows() {
      const bd = this.balanceData;
      if (!bd) return [];
      const rows = [];
      const supplierLabel = this.$t("financialSummary.balance.supplierInvoice");

      const pushSupplier = (source, shippingName, key, shippingId = null) => {
        if (!source) return;
        const amount =
          Number(source.invoiceEffective != null ? source.invoiceEffective : source.invoice) || 0;
        const payments = Array.isArray(source.linkedPayments) ? source.linkedPayments : [];
        if (amount <= 0.005 && payments.length === 0) return;
        rows.push({
          key,
          group: "__supplier__",
          shippingName,
          shippingId,
          label: supplierLabel,
          detail: "",
          contactName: source.contactName || "",
          currencyId: source.currencyId || null,
          amount,
          paid: Number(source.paid) || 0,
          requested: Number(source.requested) || 0,
          payments,
        });
      };

      const pushCost = (cost, shippingName, key, shippingId = null) => {
        if (!cost) return;
        const amount = Number(cost.amount) || 0;
        const payments = Array.isArray(cost.linkedPayments) ? cost.linkedPayments : [];
        if (amount <= 0.005 && payments.length === 0) return;
        const label = cost.name || "-";
        const detail = cost.expenseName && cost.expenseName !== label ? cost.expenseName : "";
        rows.push({
          key,
          group: label,
          shippingName,
          shippingId,
          label,
          detail,
          contactName: cost.contactName || "",
          currencyId: cost.currencyId || null,
          amount,
          paid: Number(cost.paid) || 0,
          requested: Number(cost.requested) || 0,
          payments,
        });
      };

      const ships = Array.isArray(bd.shippings) ? bd.shippings : null;
      if (ships && ships.length) {
        ships.forEach((ship, i) => {
          const shippingName = (ship && ship.name) || "";
          pushSupplier(
            {
              invoice: ship.supplierInvoice,
              invoiceEffective: ship.supplierInvoiceEffective,
              paid: ship.supplierPaid,
              requested: ship.supplierRequested,
              contactName: ship.supplierContactName,
              currencyId: ship.supplierCurrencyId,
              linkedPayments: ship.supplierLinkedPayments,
            },
            shippingName,
            `sup-${ship._id || i}`,
            ship._id
          );
          (Array.isArray(ship.costs) ? ship.costs : []).forEach((cost, j) =>
            pushCost(cost, shippingName, `cost-${ship._id || i}-${(cost && cost._id) || j}`, ship._id)
          );
        });
        return rows;
      }

      // Vista de un envío: `costs` ya viene gasto por gasto.
      const shippingName = (bd.shipping && bd.shipping.name) || "";
      pushSupplier(bd.supplier, shippingName, "sup-single");
      (Array.isArray(bd.costs) ? bd.costs : []).forEach((cost, j) =>
        pushCost(cost, shippingName, `cost-${(cost && cost._id) || j}`)
      );
      return rows;
    },
    /** `__supplier__` = factura proveedor, `__costs__` = todos los costos, o el nombre del tipo. */
    expenseBreakdownRowsFor(group) {
      return this.expenseBreakdownRows().filter((row) =>
        group === "__costs__" ? row.group !== "__supplier__" : row.group === group
      );
    },
    /** El ojo solo aparece si hay algo que mostrar: gastos para el monto, pagos para lo pagado. */
    expenseBreakdownHas(group, mode) {
      const rows = this.expenseBreakdownRowsFor(group);
      if (mode === "paid") {
        return rows.some((row) => (row.payments || []).length > 0);
      }
      return rows.length > 0;
    },
    openExpenseBreakdown(group, mode, title) {
      const rows = this.expenseBreakdownRowsFor(group);
      if (!rows.length) return;
      this.expenseBreakdown = { group, mode, title, rows };
    },
    closeExpenseBreakdown() {
      this.expenseBreakdown = null;
    },
    /** Modo pagos: una fila por pago vinculado, con el importe que aplica a ese gasto. */
    expenseBreakdownPaymentRows() {
      if (!this.expenseBreakdown) return [];
      const out = [];
      this.expenseBreakdown.rows.forEach((row) => {
        (row.payments || []).forEach((payment, i) => {
          out.push({
            key: `${row.key}-${payment._id || i}`,
            shippingName: row.shippingName,
            shippingId: row.shippingId,
            label: row.label,
            detail: row.detail,
            contactName: row.contactName,
            currencyId: row.currencyId,
            payment,
          });
        });
      });
      return out;
    },
    expenseBreakdownPaymentAmount(payment) {
      if (!payment) return 0;
      const applied = Number(payment.appliedAmount);
      return Number.isFinite(applied) && applied > 0 ? applied : Number(payment.amount) || 0;
    },
    expenseBreakdownTotal(field) {
      if (!this.expenseBreakdown) return 0;
      return this.expenseBreakdown.rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
    },
    /**
     * Total del desglose con su signo (#397): con una sola moneda, en esa; con varias, en la
     * del sistema con el tipo de cambio de la operación, como el subtotal de costos. Si falta
     * alguna cotización, la suma de siempre.
     */
    expenseBreakdownTotalDisplay(field) {
      const rows = this.expenseBreakdown ? this.expenseBreakdown.rows : [];
      const ids = new Set(rows.map((row) => String(row.currencyId || this.operationCurrencyId || "")));
      const total = this.expenseBreakdownTotal(field);
      if (ids.size <= 1) {
        return this.currencyPrefixFor(rows.length ? rows[0].currencyId : null) + this.formatNumber(total);
      }
      if (this.balanceFx) {
        let converted = 0;
        for (const row of rows) {
          const rate = this.fxEntryFor(row.currencyId || this.operationCurrencyId, row.shippingId).rate;
          if (!rate) return this.moneyPrefix + this.formatNumber(total);
          converted += (Number(row[field]) || 0) * rate;
        }
        return this.fxReportingPrefix + this.formatNumber(converted);
      }
      return this.moneyPrefix + this.formatNumber(total);
    },
    /** Nombre del contacto del pago (viene poblado desde el back). */
    expenseBreakdownPaymentContact(payment) {
      const contact = payment && payment.contact;
      if (contact && typeof contact === "object" && contact.name) return String(contact.name);
      return "";
    },

    extractClientId(field) {
      if (field == null || field === "") return null;
      if (typeof field === "object" && field._id != null) return String(field._id);
      return String(field);
    },
    extractClientName(field) {
      if (field && typeof field === "object" && field.name) return String(field.name);
      return "";
    },
    goToContact(rawId) {
      const id = this.extractClientId(rawId);
      if (!id) return;
      window.open(`/dynamic-model/Contact/view/${encodeURIComponent(id)}`, "_blank");
    },

    goToCollect(collectId) {
      if (collectId == null || collectId === "") return;
      const id =
        typeof collectId === "object" && collectId !== null && collectId._id != null
          ? String(collectId._id)
          : String(collectId);
      if (!id) return;
      window.open(`/dynamic-model/collect/view/${encodeURIComponent(id)}`, "_blank");
    },

    goToPayment(paymentId) {
      if (paymentId) {
        window.open(`/dynamic-model/payment/view/${paymentId}`, '_blank');
      }
    },

    goToShipping(shippingId) {
      if (shippingId == null || shippingId === "") return;
      const id =
        typeof shippingId === "object" && shippingId !== null && shippingId._id != null
          ? String(shippingId._id)
          : String(shippingId);
      if (!id) return;
      window.open(`/dynamic-model/shipping/view/${encodeURIComponent(id)}`, "_blank");
    },

    // Open receipt in new tab
    openReceiptModal(collectId) {
      window.open(`/templates/receipt/${collectId}`, '_blank');
    },

    /**
     * Fecha de un campo fecha del documento (cobro, pago): se muestra el día civil
     * guardado, sin reinterpretarlo en la zona del navegador. `new Date(...)` leía
     * "2026-09-08" como medianoche UTC y en UTC-3 lo mostraba como el 7.
     */
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return formatDateSync(dateString, "N/A");
    },

    // View cash flow details (collections + payments)
    async viewCashFlowDetails() {
      try {
        this.showCashFlowModal = true;
        this.loadingCashFlow = true;
        this.cashFlowData = null;

        const targetId = this.itemData._id;
        if (!targetId) {
          throw new Error("No valid ID found");
        }

        // Load collections and payments in parallel
        const [collectionsResponse, paymentsResponse] = await Promise.all([
          wencesApi.get(`/api/financial/collects/${targetId}/details`),
          wencesApi.get(`/api/financial/payments/${targetId}/paid`)
        ]);
        
        const collections = collectionsResponse.data.success ? collectionsResponse.data.collections || [] : [];
        const totalCollected = collectionsResponse.data.success ? collectionsResponse.data.collectedAmount || 0 : 0;

        // Cash flow "Total Paid": only confirmed paid amounts scoped to this shipping's expenses
        const paymentsRaw = paymentsResponse.data.success ? paymentsResponse.data.payments || [] : [];
        const payments =
          paymentsResponse.data.success && Array.isArray(paymentsResponse.data.paidPayments)
            ? paymentsResponse.data.paidPayments
            : paymentsRaw.filter((p) => p && p.paid);
        const totalPaid = paymentsResponse.data.success ? paymentsResponse.data.totalPaid || 0 : 0;

        // El margen actual sale del único cálculo del sistema (back: calculateMargins).
        const currentMargin = paymentsResponse.data.success ? paymentsResponse.data.currentMargin : null;

        this.cashFlowData = {
          collections,
          payments,
          totalCollected,
          totalPaid,
          currentMargin,
          /** Cobrado y pagado en la moneda del margen (exchange_rates_module, #397). */
          marginFigures: paymentsResponse.data.success ? paymentsResponse.data.marginFigures || null : null,
        };
      } catch (error) {
        console.error("Error loading cash flow details:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.errorLoadingCashFlow"),
        });
        this.showCashFlowModal = false;
      } finally {
        this.loadingCashFlow = false;
      }
    },

    closeCashFlowModal() {
      this.showCashFlowModal = false;
      this.cashFlowData = null;
    },

    async handleCollectionSaved(collectionData) {
      try {
        console.log("Collection saved:", collectionData);

        Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: this.$tn("financialSummary.collectionCreatedSuccessfully"),
          timer: 2000,
          showConfirmButton: false,
        });

        // Close modal
        this.closeAddCollectionModal();

        // Reload financial data to reflect the new collection
        await this.loadFinancialData();
        // Reload balance data to update Operation Balance section
        await this.loadBalanceData();
        
        // Emit event to parent component to refresh if needed
        this.$emit("collection-saved", collectionData);
        this.$emit("financial-data-changed");
      } catch (error) {
        console.error("Error handling collection save:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.failedToSaveCollection"),
        });
      }
    },

    /**
     * Misma regla que al crear referencia shipping → payment: exige expense tipo invoice/proforma supplier.
     * Solo aplica cuando el modal abrirá con parentModel=shipping (no ruta prepayment → shipping_expenses).
     */
    async verifyShippingPaymentPrerequisite(paymentType) {
      if (!this.isShipping || !this.itemData?._id) {
        return true;
      }
      const usesShippingParent = !(
        (paymentType === "full" || paymentType === "balance") &&
        this.paymentData?.prepaymentExpenseId
      );
      if (!usesShippingParent) {
        return true;
      }
      try {
        await wencesApi.get("/api/gotrading/model-reference-mapping", {
          params: {
            sourceModel: "shipping",
            targetModel: "payment",
            sourceRecordId: this.itemData._id,
          },
        });
        return true;
      } catch (error) {
        const code = error.response?.data?.code;
        if (code === "SHIPPING_EXPENSE_REQUIRED") {
          const messageKey = error.response?.data?.messageKey;
          const msg = messageKey ? this.$t(messageKey) : null;
          const text =
            msg && msg !== messageKey
              ? msg
              : error.response?.data?.error || this.$t("paymentOrderRequiresExpense");
          await Swal.fire({
            icon: "warning",
            title: this.$tn("paymentOrderRequiresExpenseTitle") || "Attention",
            text,
          });
          return false;
        }
        throw error;
      }
    },

    async openAddPaymentModal(paymentType = "advance") {
      const allowed = await this.verifyShippingPaymentPrerequisite(paymentType);
      if (!allowed) {
        return;
      }
      try {
        // For full and balance payments, use shipping_expenses as parent
        // For advance payments, use shipping as parent
        if ((paymentType === "full" || paymentType === "balance") && this.paymentData?.prepaymentExpenseId) {
          this.parentID = this.paymentData.prepaymentExpenseId;
          this.parentModel = "shipping_expenses";
        } else {
          this.parentID = this.itemData._id;
          this.parentModel = "shipping";
        }
        
        // Pass paymentType to extraParams so the hook can use it
        this.extraParams = { paymentType: paymentType };
        
        // Load lists for dropdowns
        await this.loadModalData();
        
        // Show modal
        this.showAddPaymentModal = true;
      } catch (error) {
        console.error("Error opening add payment modal:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.payments.failedToOpenPaymentModal"),
        });
      }
    },

    openEditShippingExpenseModal(expenseId) {
      if (!expenseId) return;
      this.quickEditShippingExpenseId = String(expenseId);
      this.showQuickEditShippingExpenseModal = true;
    },

    closeQuickEditShippingExpenseModal() {
      this.showQuickEditShippingExpenseModal = false;
      this.quickEditShippingExpenseId = null;
    },

    async handleQuickEditShippingExpenseSaved() {
      this.closeQuickEditShippingExpenseModal();
      try {
        await this.loadPaymentData();
        await this.loadBalanceData();
      } catch (e) {
        console.error("handleQuickEditShippingExpenseSaved:", e);
      }
    },

    openPayExpenseModal(expenseId, expenseName, amountMax, amountSuggested, options = {}) {
      this.quickRequestPaymentExpenseId = expenseId ? String(expenseId) : null;
      this.quickRequestPaymentExpenseName = expenseName || "";
      const cap = Number(amountMax);
      this.quickRequestPaymentAmountMax =
        Number.isFinite(cap) && cap > 0 ? cap : null;
      const sug = Number(amountSuggested);
      this.quickRequestPaymentAmountSuggested =
        Number.isFinite(sug) && sug >= 0.01 ? sug : null;
      this.quickRequestPaymentSplitSupplierMode = !!options.splitSupplierMode;
      this.quickRequestPaymentSplitShippingId =
        options && options.shippingId != null && options.shippingId !== ""
          ? String(options.shippingId)
          : null;
      this.quickRequestPaymentExtraParams = {
        type: "expense",
        expenseName: expenseName || "",
      };
      this.showQuickRequestPaymentModal = true;
    },

    closeQuickRequestPaymentModal() {
      this.showQuickRequestPaymentModal = false;
      this.quickRequestPaymentExpenseId = null;
      this.quickRequestPaymentExpenseName = "";
      this.quickRequestPaymentAmountMax = null;
      this.quickRequestPaymentAmountSuggested = null;
      this.quickRequestPaymentSplitSupplierMode = false;
      this.quickRequestPaymentSplitShippingId = null;
      this.quickRequestPaymentExtraParams = {};
    },

    async handleQuickRequestPaymentSaved(paymentData) {
      this.closeQuickRequestPaymentModal();
      try {
        await this.loadPaymentData();
        await this.loadBalanceData();
        this.$emit("payment-saved", paymentData);
      } catch (error) {
        console.error("handleQuickRequestPaymentSaved:", error);
      }
    },

    closeAddPaymentModal() {
      this.showAddPaymentModal = false;
      this.parentID = null;
      this.parentModel = null;
      this.extraParams = {};
    },

    async handlePaymentSaved(paymentData) {
      try {
        console.log("Payment saved:", paymentData);

        Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: this.$tn("financialSummary.payments.paymentCreatedSuccessfully"),
          timer: 2000,
          showConfirmButton: false,
        });

        // Close modal
        this.closeAddPaymentModal();

        // Reload payment data to reflect the new payment
        await this.loadPaymentData();
        // Reload balance data to update Operation Balance section
        await this.loadBalanceData();
        
        // Emit event to parent component to refresh if needed
        this.$emit("payment-saved", paymentData);
      } catch (error) {
        console.error("Error handling payment save:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.payments.failedToSavePayment"),
        });
      }
    },

    showHelp() {
      const helpContent = `
        <div class="text-start">
          <h6>💰 ${this.$t("financialSummary.help.advanceTitle")}</h6>
          <p>${this.$t("financialSummary.help.advanceDescription")}</p>

          <h6>📊 ${this.$t("financialSummary.help.calculationTitle")}</h6>
          <p>${this.$t("financialSummary.help.calculationDescription")}</p>

          <h6>🚢 ${this.$t("financialSummary.help.shippingTitle")}</h6>
          <p>${this.$t("financialSummary.help.shippingDescription")}</p>

          <h6>📝 ${this.$t("financialSummary.help.exampleTitle")}</h6>
          <ul>
            <li>${this.$t("financialSummary.help.example1")}</li>
            <li>${this.$t("financialSummary.help.example2")}</li>
            <li>${this.$t("financialSummary.help.example3")}</li>
          </ul>
        </div>
      `;

      Swal.fire({
        title: this.$tn("financialSummary.helpTitle"),
        html: helpContent,
        icon: "info",
        confirmButtonText: this.$tn("financialSummary.close"),
        confirmButtonColor: "#17a2b8",
        width: "600px",
      });
    },

    /** Los importes del desglose están en la moneda del margen (USD con tipo de cambio, #397). */
    moneyForMarginHelp(value) {
      return this.marginMoneyPrefix + this.formatMarginAmount(value);
    },

    buildMarginExplanationFallback() {
      const m = this.balanceData?.margins || {};
      const supplier = this.balanceData?.supplier || {};
      const costs = this.balanceData?.costsSummary || {};
      const proformTotal = Number(m.proformTotal) || 0;
      const shippingTotal = Number(m.shippingTotal) || 0;
      const projected = Number(m.projected) || 0;
      const real = Number(m.real) || 0;
      const current = Number(m.current) || 0;
      const supplierInvoice = Number(supplier.invoiceEffective ?? supplier.invoice) || 0;
      const otherCosts = Number(costs.total) || 0;
      const estimatedCosts =
        Math.round((proformTotal - projected) * 100) / 100 ||
        Math.round((shippingTotal - real) * 100) / 100;
      const bankCommissions =
        Math.round((estimatedCosts - supplierInvoice - otherCosts) * 100) / 100;
      const supplierPaid = Number(supplier.paid) || 0;
      const otherCostsPaid = Number(costs.paid) || 0;
      const totalPaid = Math.round((supplierPaid + otherCostsPaid) * 100) / 100;
      const cashIn = Math.round((current + totalPaid) * 100) / 100;
      return {
        estimatedCosts,
        supplierInvoice,
        otherCosts,
        bankCommissions,
        collectedApplied: cashIn,
        creditNotes: 0,
        cashIn,
        totalPaid,
        supplierPaid,
        otherCostsPaid,
      };
    },

    showMarginHelp(kind) {
      if (!this.balanceData?.margins) return;

      const m = this.balanceData.margins;
      const exp = m.explanation || this.buildMarginExplanationFallback();
      const money = (v) => this.moneyForMarginHelp(v);
      // Lo del cliente también en la moneda de la Invoice, si el margen va en otra (#397):
      // «$28,118.18 (€25,110.00)», así no se confunde con el cobrado que se ve arriba en €.
      const original = exp.original || null;
      const withOriginal = (v, key) => {
        const raw = original ? Number(original[key]) : NaN;
        if (!Number.isFinite(raw)) return money(v);
        return `${money(v)} <span class="fw-normal text-muted">(${this.currencyPrefixFor(original.currencyId)}${this.formatMarginAmount(raw)})</span>`;
      };
      const titles = {
        projected: this.$t("financialSummary.balance.projectedMargin"),
        real: this.$t("financialSummary.balance.realMargin"),
        current: this.$t("financialSummary.balance.currentMargin"),
      };
      // El nombre del margen, igual que en su tarjeta.
      const title = `<span data-app-language>${titles[kind] || titles.projected}</span>`;
      // El % junto al resultado, con la misma base que la tarjeta (#505 duntower): el popup
      // mostraba solo la plata.
      const bases = {
        projected: this.projectedMarginBase,
        real: this.realMarginBase,
        current: this.currentMarginBase,
      };
      const withPct = (value, k) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return money(value);
        return `${money(value)} <span class="fw-normal">(${n >= 0 ? "+" : ""}${this.formatMarginPercent(n, bases[k])}%)</span>`;
      };

      // NC − ND: la NC baja lo que le cobramos al cliente y la ND lo sube.
      const clientNotes = Number(exp.clientNotes) || 0;
      const clientNotesRow =
        Math.abs(clientNotes) > 0.009
          ? `<div class="d-flex justify-content-between"><span>${
              clientNotes > 0 ? "−" : "+"
            } ${this.$t("financialSummary.balance.marginHelp.clientNotes")}</span><strong>${money(
              Math.abs(clientNotes)
            )}</strong></div>`
          : "";

      // La fórmula depende del plan de la operación (back: calculateMargins, MARGIN_PLANS).
      const planDetail = exp.planDetail || null;
      const fin = exp.plan === "negotiation" ? planDetail : null;
      const help = (key) => this.$t("financialSummary.balance.marginHelp." + key);
      // Lo que explica el margen va en el idioma de avisos; los conceptos y los importes
      // (el cuadro, con data-app-language) en el de la app, para que se llamen igual que
      // en el Operation Balance (#397).
      const helpText = (key) => this.$tn("financialSummary.balance.marginHelp." + key);

      let formulaHtml = "";
      if (kind === "projected" && fin && fin.grossMargin != null) {
        const interestLabel =
          fin.interestSource === "real"
            ? help("interestReal")
            : fin.interestSource === "projected"
              ? help("interestProjected")
              : help("estimatedInterest");
        const interestForMargin =
          fin.interestForMargin != null ? fin.interestForMargin : fin.estimatedInterest;
        formulaHtml = `
          <p class="mb-2">${helpText("fobProjectedIntro")}</p>
          <div class="border rounded p-2 mb-2 bg-light text-start small" data-app-language>
            <div class="d-flex justify-content-between"><span>${help("grossMargin")}</span><strong>${money(fin.grossMargin)}</strong></div>
            <div class="d-flex justify-content-between"><span>+ ${help("estimatedInterest")}</span><strong>${money(fin.estimatedInterest)}</strong></div>
            <div class="d-flex justify-content-between text-danger"><span>− ${interestLabel}</span><strong>${money(interestForMargin)}</strong></div>
            <hr class="my-2"/>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.projectedMargin")}</span><strong>${withPct(m.projected, "projected")}</strong></div>
          </div>
          <p class="mb-0 small text-muted">${helpText("fobProjectedNote")}</p>
        `;
      } else if (kind === "projected" && exp.plan === "proformExpenses" && planDetail) {
        formulaHtml = `
          <p class="mb-2">${helpText("proformPlanIntro")}</p>
          <div class="border rounded p-2 mb-2 bg-light text-start small" data-app-language>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.proformaTotal")}</span><strong>${money(m.proformTotal)}</strong></div>
            ${clientNotesRow}
            <div class="d-flex justify-content-between text-danger"><span>− ${help("proformPlanCosts")}</span><strong>${money(planDetail.plannedCosts)}</strong></div>
            <hr class="my-2"/>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.projectedMargin")}</span><strong>${withPct(m.projected, "projected")}</strong></div>
          </div>
          <p class="mb-0 small text-muted">${helpText("proformPlanNote")}</p>
        `;
      } else if (kind === "projected") {
        formulaHtml = `
          <p class="mb-2">${this.$tn("financialSummary.balance.marginHelp.projectedIntro")}</p>
          <div class="border rounded p-2 mb-2 bg-light text-start small" data-app-language>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.proformaTotal")}</span><strong>${withOriginal(m.proformTotal, "proformTotal")}</strong></div>
            ${clientNotesRow}
            <div class="d-flex justify-content-between text-danger"><span>− ${this.$t("financialSummary.balance.marginHelp.estimatedCosts")}</span><strong>${money(exp.estimatedCosts)}</strong></div>
            <hr class="my-2"/>
            <div class="ps-2 text-muted">
              <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.supplierInvoice")}</span><span>${money(exp.supplierInvoice)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.costsSubtotal")}</span><span>${money(exp.otherCosts)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.marginHelp.bankCommissions")}</span><span>${money(exp.bankCommissions)}</span></div>
            </div>
            <hr class="my-2"/>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.projectedMargin")}</span><strong>${withPct(m.projected, "projected")}</strong></div>
          </div>
          <p class="mb-0 small text-muted">${this.$tn("financialSummary.balance.marginHelp.projectedNote")}</p>
        `;
      } else if (kind === "real") {
        formulaHtml = `
          <p class="mb-2">${this.$tn("financialSummary.balance.marginHelp.realIntro")}</p>
          <div class="border rounded p-2 mb-2 bg-light text-start small" data-app-language>
            <div class="d-flex justify-content-between"><span>${this.$t(this.balanceData.isProform ? "financialSummary.balance.totalInvoices" : "financialSummary.balance.totalInvoice")}</span><strong>${withOriginal(m.shippingTotal, "invoiceTotal")}</strong></div>
            ${clientNotesRow}
            <div class="d-flex justify-content-between text-danger"><span>− ${this.$t("financialSummary.balance.marginHelp.estimatedCosts")}</span><strong>${money(exp.estimatedCosts)}</strong></div>
            <hr class="my-2"/>
            <div class="ps-2 text-muted">
              <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.supplierInvoice")}</span><span>${money(exp.supplierInvoice)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.costsSubtotal")}</span><span>${money(exp.otherCosts)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.marginHelp.bankCommissions")}</span><span>${money(exp.bankCommissions)}</span></div>
            </div>
            <hr class="my-2"/>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.realMargin")}</span><strong>${withPct(m.real, "real")}</strong></div>
          </div>
          <p class="mb-0 small text-muted">${this.$tn("financialSummary.balance.marginHelp.realNote")}</p>
        `;
      } else {
        const creditNotes = Number(exp.creditNotes) || 0;
        const hasCreditNotes = Math.abs(creditNotes) > 0.009;
        const cashInBreakdown = hasCreditNotes
          ? `<div class="ps-2 text-muted">
              <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.marginHelp.collectedApplied")}</span><span>${money(exp.collectedApplied)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.marginHelp.creditNotes")}</span><span>${money(creditNotes)}</span></div>
            </div>`
          : "";
        formulaHtml = `
          <p class="mb-2">${this.$tn("financialSummary.balance.marginHelp.currentIntro")}</p>
          <div class="border rounded p-2 mb-2 bg-light text-start small" data-app-language>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.marginHelp.cashIn")}</span><strong>${withOriginal(exp.cashIn, "collected")}</strong></div>
            ${cashInBreakdown}
            <div class="d-flex justify-content-between text-danger mt-1"><span>− ${this.$t("financialSummary.balance.marginHelp.confirmedPaid")}</span><strong>${money(exp.totalPaid)}</strong></div>
            <div class="ps-2 text-muted">
              <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.marginHelp.supplierPaid")}</span><span>${money(exp.supplierPaid)}</span></div>
              <div class="d-flex justify-content-between"><span>+ ${this.$t("financialSummary.balance.marginHelp.costsPaid")}</span><span>${money(exp.otherCostsPaid)}</span></div>
            </div>
            <hr class="my-2"/>
            <div class="d-flex justify-content-between"><span>${this.$t("financialSummary.balance.currentMargin")}</span><strong>${withPct(m.current, "current")}</strong></div>
          </div>
          <p class="mb-0 small text-muted">${this.$tn("financialSummary.balance.marginHelp.currentNote")}</p>
        `;
      }

      Swal.fire({
        title,
        html: `<div class="text-start">${formulaHtml}</div>`,
        icon: "info",
        confirmButtonText: this.$tn("financialSummary.close"),
        confirmButtonColor: "#17a2b8",
        width: "560px",
      });
    },
  },
};
</script>

<style scoped>
.financial-summary-stages {
  background: linear-gradient(145deg, rgba(13, 110, 253, 0.04) 0%, #fff 35%, #f8f9fa 100%);
  border-color: rgba(13, 110, 253, 0.18) !important;
}

.financial-summary-stages__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.35rem;
  border-radius: 0.35rem;
  font-weight: 800;
  font-size: 0.85rem;
  background: #0d6efd;
  color: #fff;
}

.financial-summary-stages__badge--secondary {
  background: #6c757d;
}

.financial-summary-stages__badge--done {
  background: #198754 !important;
  color: #fff !important;
}

.financial-summary-stages__badge--secondary.financial-summary-stages__badge--done {
  background: #198754 !important;
}

.financial-summary-stages__col--done {
  background: rgba(25, 135, 84, 0.07);
}

.financial-summary-stages__stage-head--done {
  padding-bottom: 0.15rem;
}

.financial-summary-stages__col1 {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

@media (min-width: 992px) {
  .financial-summary-stages__col1 {
    border-bottom: none;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
}

.financial-summary-stages__ship {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 0.08rem 0.35rem rgba(0, 0, 0, 0.04);
}

.financial-summary-stages__ship--complete {
  background: rgba(25, 135, 84, 0.08);
  border-color: rgba(25, 135, 84, 0.38) !important;
  box-shadow: 0 0.12rem 0.45rem rgba(25, 135, 84, 0.14);
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-section--invoice {
  background: rgba(25, 135, 84, 0.1);
  border-left-color: #198754;
}

.financial-summary-stages__ship--complete
  .financial-summary-stages__ship-section--invoice
  .financial-summary-stages__ship-section-title {
  color: #198754 !important;
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-name-link {
  color: #146c43 !important;
}

.financial-summary-stages__ship--complete .financial-summary-stages__ship-name-link:hover {
  color: #0f5132 !important;
}

.financial-summary-stages__ship--placeholder {
  border: 2px dashed rgba(108, 117, 125, 0.65);
  background: rgba(248, 249, 250, 0.85);
  box-shadow: none;
  min-height: 6.5rem;
}

.financial-summary-stages__ship-name-link {
  text-decoration: none;
  cursor: pointer;
}

.financial-summary-stages__ship-name-link:hover {
  color: var(--bs-primary) !important;
  text-decoration: underline;
}

.financial-summary-stages__ship-row {
  align-items: stretch;
}

.financial-summary-stages__progress {
  border-radius: 0.25rem;
  background-color: #e9ecef;
}

.financial-summary-stages__chip {
  cursor: grab;
}

.financial-summary-stages__chip:active {
  cursor: grabbing;
}

.financial-summary-stages__chip-mini {
  cursor: grab;
  max-width: 7rem;
}

.financial-summary-stages__dropzone {
  background: rgba(13, 110, 253, 0.04);
}

.financial-summary-stages__ship-section {
  border-radius: 0.35rem;
  padding: 0.5rem 0.55rem;
}

.financial-summary-stages__ship-section-title {
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.financial-summary-stages__ship-section--advance {
  background: rgba(13, 202, 240, 0.09);
  border-left: 3px solid #0dcaf0;
}

.financial-summary-stages__ship-section--invoice {
  background: rgba(108, 117, 125, 0.08);
  border-left: 3px solid #868e96;
}

.financial-summary-stages .ship-stage-invoice-pending {
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
}

/* Cobro solo factura embarque: solo lectura (no es chip arrastrable como el adelanto) */
.financial-summary-stages__invoice-collected-static {
  cursor: default;
  user-select: text;
}

.financial-summary-stages__chip-invoice-only {
  cursor: default;
}

.financial-summary-stages__chip-invoice-only:active {
  cursor: default;
}

.financial-summary-client-link {
  text-decoration: none;
  cursor: pointer;
}

.financial-summary-client-link:hover {
  color: var(--bs-primary) !important;
  text-decoration: underline;
}
</style>
